import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getChatMessages, sendChatMessage, getMembers, onCacheUpdate, refreshMembers, isAdminLoggedIn } from '../services/api';
import CallOverlay from './CallOverlay';
import MiniWIA from './MiniWIA';
import WchatIntro from './WchatIntro';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import '../styles/wclub-effects.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Clear app badge when viewing chat
const clearAppBadge = async () => {
  try {
    // Clear via main thread Badging API
    if (navigator.clearAppBadge) {
      await navigator.clearAppBadge();
    }
    // Also set to 0 explicitly (iOS PWA needs this)
    if (navigator.setAppBadge) {
      await navigator.setAppBadge(0);
    }
    // Also tell service worker to clear
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if (reg.active) {
        reg.active.postMessage({ type: 'CLEAR_BADGE' });
      }
    }
  } catch (e) {}
};

const b64toBlob = (b64, type = 'audio/webm') => {
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type });
  } catch (e) { return null; }
};

const getRecordingMime = () => {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return '';
};

/* ─── Icon Components (White outline style) ─── */
const IconAttach = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);
const IconMic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3" /><path d="M19 10v1a7 7 0 01-14 0v-1" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);
const IconSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
  </svg>
);
const IconPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);
const IconBell = ({ active }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);
const IconChevron = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconStop = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
);
const IconCamera = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" />
  </svg>
);

/* ─── Reaction Icons (For message reactions) ─── */
const ReactionIcons = {
  heart: ({ size = 16, filled = false, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  fire: ({ size = 16, filled = false, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  ),
  thumbs_up: ({ size = 16, filled = false, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  ),
  laugh: ({ size = 16, filled = false, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
  wow: ({ size = 16, filled = false, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="16" r="2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
};

const REACTION_CONFIG = {
  heart: { color: '#ef4444', label: 'Curtir' },
  fire: { color: '#f97316', label: 'Fogo' },
  thumbs_up: { color: '#3b82f6', label: 'Legal' },
  laugh: { color: '#eab308', label: 'Haha' },
  wow: { color: '#8b5cf6', label: 'Uau' },
};

// ===== W AVISO TOOLTIP - Liquid Glass centered overlay =====
const WAvisoTooltip = ({ aviso, onClose, onDelete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState('0:00');
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); } 
    else { audioRef.current.play().catch(() => {}); }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
      const remaining = Math.ceil((audio.duration || 0) - (audio.currentTime || 0));
      setDuration(`${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`);
    };
    const onEnd = () => { setIsPlaying(false); setProgress(0); };
    const onLoaded = () => {
      const d = Math.ceil(audio.duration || 0);
      setDuration(`${Math.floor(d / 60)}:${String(d % 60).padStart(2, '0')}`);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('loadedmetadata', onLoaded);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnd); audio.removeEventListener('loadedmetadata', onLoaded); };
  }, []);

  const label = aviso.message?.replace(/^\[.*?\]\s*/, '') || 'Mensagem do W';
  const typeMatch = aviso.message?.match(/^\[(\w+)\]/);
  const avisoType = typeMatch ? typeMatch[1] : 'AVISO';
  const ts = aviso.timestamp ? new Date(aviso.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80]"
        style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
        onClick={onClose} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[81] flex items-center justify-center px-5"
        style={{ pointerEvents: 'none' }}
      >
        <motion.div
          initial={{ opacity: 0, scaleX: 0.1, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleX: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleX: 0.1, scaleY: 0.5 }}
          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          className="w-full max-w-[320px] relative"
          style={{ pointerEvents: 'auto' }}
          data-testid="w-aviso-tooltip"
        >
        {/* LED border animation - continuous like member cards */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-20" viewBox="0 0 320 400" preserveAspectRatio="none">
          <defs>
            <linearGradient id="avisoLedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="40%" stopColor="rgba(96,165,250,0.6)" />
              <stop offset="50%" stopColor="rgba(200,220,255,0.9)" />
              <stop offset="60%" stopColor="rgba(96,165,250,0.6)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>
          <rect x="1" y="1" width="318" height="398" rx="24" ry="24" fill="none" stroke="url(#avisoLedGrad)" strokeWidth="1.5" strokeDasharray="1440" className="aviso-led-continuous" />
        </svg>
        <style>{`
          @keyframes avisoLedLoop { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -2880; } }
          .aviso-led-continuous { animation: avisoLedLoop 6s linear infinite; }
        `}</style>

        <div className="rounded-[24px] overflow-hidden relative"
          style={{
            background: 'rgba(8,12,24,0.60)',
            backdropFilter: 'blur(40px) saturate(2)',
            WebkitBackdropFilter: 'blur(40px) saturate(2)',
            border: '1px solid rgba(59,130,246,0.12)',
            boxShadow: '0 0 60px rgba(59,130,246,0.06), 0 20px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(120,170,255,0.08)',
          }}>

          {/* X close */}
          <button onClick={onClose}
            className="absolute top-3.5 right-3.5 w-7 h-7 rounded-full flex items-center justify-center z-10 active:scale-90 transition"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="w-aviso-close">
            <svg className="w-3 h-3 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* W logo */}
          <div className="pt-6 pb-3 px-6 flex flex-col items-center">
            <motion.div 
              animate={{ boxShadow: ['0 0 15px rgba(59,130,246,0.25)', '0 0 30px rgba(59,130,246,0.45)', '0 0 15px rgba(59,130,246,0.25)'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="w-12 h-12 rounded-full mb-3 flex items-center justify-center"
              style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}>
              <span className="text-[20px] font-['Jost'] font-bold text-blue-400/90">W</span>
            </motion.div>
            <div className="flex items-center gap-2">
              <span className="text-[8px] font-['Jost'] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(59,130,246,0.08)', color: 'rgba(96,165,250,0.7)', border: '1px solid rgba(59,130,246,0.12)' }}>
                {avisoType}
              </span>
              {ts && <span className="text-[9px] text-white/20 font-['Jost']">{ts}</span>}
            </div>
          </div>

          {/* Message */}
          <div className="px-5 pb-4">
            <p className="text-white/70 text-[13px] font-['Jost'] text-center leading-[1.7] font-light">{label}</p>
          </div>

          {/* Audio player - WhatsApp style waveform */}
          {aviso.file_data && (
            <div className="px-4 pb-4">
              <audio ref={audioRef} src={aviso.file_data} preload="metadata" />
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-2xl"
                style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.08)' }}>
                <motion.button whileTap={{ scale: 0.8 }} onClick={togglePlay}
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 active:opacity-70"
                  style={{ background: isPlaying ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)' }}>
                  {isPlaying ? (
                    <svg className="w-3.5 h-3.5" fill="rgb(96,165,250)" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 ml-0.5" fill="rgb(96,165,250)" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </motion.button>
                {/* Waveform bars - WhatsApp style */}
                <div className="flex-1 flex items-center gap-[2px] h-7 min-w-0">
                  {Array.from({ length: 32 }, (_, i) => {
                    const h = [4,7,3,9,5,11,6,8,4,10,7,12,5,9,3,11,8,6,10,4,9,7,5,12,8,3,10,6,11,4,7,9][i] || 5;
                    const barProgress = (i / 32) * 100;
                    const isPast = barProgress < progress;
                    return (
                      <div key={i} className="flex-1 flex items-center justify-center">
                        <motion.div 
                          animate={isPlaying && isPast ? { scaleY: [1, 0.4 + Math.random() * 0.8, 1] } : { scaleY: 1 }}
                          transition={isPlaying ? { duration: 0.3 + Math.random() * 0.2, repeat: Infinity, ease: 'easeInOut' } : {}}
                          className="w-[2.5px] rounded-full transition-colors duration-150"
                          style={{ 
                            height: `${h * 2.2}px`,
                            background: isPast ? 'rgba(96,165,250,0.8)' : 'rgba(96,165,250,0.2)',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                <span className="text-[10px] text-blue-400/35 font-['Jost'] font-medium flex-shrink-0 w-8 text-right">{duration}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-4 pb-4 flex gap-2">
            <button onClick={(e) => { e.stopPropagation(); onDelete?.(aviso.id); }}
              className="flex-1 py-2 rounded-xl text-[10px] font-['Jost'] text-white/20 active:bg-red-500/10 transition"
              style={{ border: '1px solid rgba(255,255,255,0.04)' }}
              data-testid="w-aviso-delete">
              Apagar
            </button>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="flex-1 py-2 rounded-xl text-[10px] font-['Jost'] text-blue-400/50 active:bg-blue-500/10 transition font-medium"
              style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.10)' }}>
              Entendido
            </button>
          </div>
        </div>
      </motion.div>
      </motion.div>
    </>
  );
};

// ===== ADMIN W AVISO COMPOSE PANEL =====
const AdminWAvisoPanel = ({ allMembers, currentMember, API, onSent }) => {
  const [text, setText] = useState('');
  const [type, setType] = useState('aviso');
  const [target, setTarget] = useState('individual');
  const [targetMember, setTargetMember] = useState(null);
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const types = [
    { id: 'aviso', label: 'Aviso' },
    { id: 'lembrete', label: 'Lembrete' },
    { id: 'parabens', label: 'Parabéns' },
    { id: 'alerta', label: 'Alerta' },
  ];

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    if (target === 'individual' && !targetMember) {
      setError('Selecione um sócio');
      return;
    }
    setSending(true);
    setError('');
    try {
      const recipientId = target === 'all' ? 'all' : targetMember.id;
      await axios.post(`${API}/w-avisos/send`, { text: text.trim(), recipient_id: recipientId, type });
      setSent(true);
      setText('');
      setTimeout(() => setSent(false), 3000);
      onSent?.();
    } catch (e) {
      setError('Erro ao enviar');
      console.error('[W Aviso]', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div 
      initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
      className="overflow-hidden border-b"
      style={{ borderColor: 'rgba(59,130,246,0.08)' }}
    >
      <div className="px-3 pt-3 pb-2">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <span className="text-[10px] font-['Jost'] font-bold text-blue-400">W</span>
          </div>
          <span className="text-[10px] font-['Jost'] text-blue-400/60 uppercase tracking-widest">Enviar Aviso</span>
          {sent && (
            <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} 
              className="text-[9px] text-emerald-400/70 font-['Jost'] ml-auto flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              Enviado
            </motion.span>
          )}
          {error && <span className="text-[9px] text-red-400/60 font-['Jost'] ml-auto">{error}</span>}
        </div>

        {/* Type pills */}
        <div className="flex gap-1.5 mb-2.5 overflow-x-auto">
          {types.map(t => (
            <button key={t.id} onClick={() => setType(t.id)}
              className="px-2.5 py-1 rounded-full text-[9px] font-['Jost'] font-medium whitespace-nowrap transition active:scale-95"
              style={{
                background: type === t.id ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${type === t.id ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)'}`,
                color: type === t.id ? 'rgba(96,165,250,0.9)' : 'rgba(255,255,255,0.3)',
              }}
              data-testid={`aviso-type-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Target selector */}
        <div className="flex gap-1.5 mb-2.5">
          <button onClick={() => { setTarget('individual'); setShowTargetPicker(!showTargetPicker); }}
            className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[10px] font-['Jost'] transition active:scale-[0.98]"
            style={{
              background: target === 'individual' ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${target === 'individual' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)'}`,
              color: target === 'individual' ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.3)',
            }}
            data-testid="aviso-target-individual">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            {targetMember ? targetMember.name.split(' ')[0] : 'Sócio'}
          </button>
          <button onClick={() => { setTarget('all'); setTargetMember(null); setShowTargetPicker(false); }}
            className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[10px] font-['Jost'] transition active:scale-[0.98]"
            style={{
              background: target === 'all' ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${target === 'all' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)'}`,
              color: target === 'all' ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.3)',
            }}
            data-testid="aviso-target-all">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            Todos
          </button>
        </div>

        {/* Member picker */}
        <AnimatePresence>
          {showTargetPicker && target === 'individual' && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="mb-2 max-h-[120px] overflow-y-auto rounded-xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
              {allMembers.filter(m => m.id !== currentMember?.id).map(m => (
                <button key={m.id} onClick={() => { setTargetMember(m); setShowTargetPicker(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] font-['Jost'] text-white/60 active:bg-blue-500/10 transition border-b border-white/[0.02]"
                  data-testid={`aviso-member-${m.id}`}>
                  <span className="text-[8px] text-blue-400/40 w-5 text-center font-semibold">{m.name?.charAt(0)}</span>
                  <span>{m.name}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compose */}
        <div className="flex gap-2">
          <input
            type="text" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            placeholder={`Mensagem do W...`}
            className="flex-1 px-3 py-2 rounded-xl text-[12px] font-['Jost'] text-white placeholder-white/20 outline-none"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(59,130,246,0.08)' }}
            data-testid="aviso-text-input"
          />
          <motion.button 
            whileTap={{ scale: 0.85 }} onClick={handleSend}
            disabled={!text.trim() || sending}
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition disabled:opacity-20"
            style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)' }}
            data-testid="aviso-send-btn">
            {sending ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 rounded-full border-2 border-blue-400/30 border-t-blue-400" />
            ) : (
              <svg className="w-4 h-4 text-blue-400/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// ===== LOCATION PIN MESSAGE COMPONENT =====
const LocationPinMessage = ({ message, isOwn, formatTime, timestamp }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Parse location data
  const lines = (message || '').split('\n');
  const placeName = lines[0]?.replace(/^📍\s*/, '') || 'Localização';
  const address = lines.find(l => l && !l.startsWith('📍') && !l.startsWith('💬') && !l.match(/https?:\/\//)) || '';
  const mapUrl = lines.find(l => l.match(/https?:\/\//))?.match(/(https?:\/\/[^\s]+)/)?.[1] || '';
  const comment = lines.find(l => l.startsWith('💬'))?.replace(/^💬\s*/, '') || '';

  const handleClick = (e) => {
    e.stopPropagation();
    if (expanded && mapUrl) {
      window.open(mapUrl, '_blank', 'noopener');
    } else {
      setExpanded(!expanded);
    }
  };

  return (
    <div className="cursor-pointer select-none" onClick={handleClick} data-testid="location-pin">
      {/* Pin Icon Row */}
      <div className="flex items-center gap-2.5">
        <div className="relative">
          {/* Animated pulse ring */}
          {!expanded && (
            <motion.div 
              animate={{ scale: [1, 1.8], opacity: [0.4, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full"
              style={{ border: '1.5px solid rgba(34,211,238,0.5)' }}
            />
          )}
          {/* Pin circle */}
          <motion.div 
            animate={{ 
              boxShadow: expanded 
                ? '0 0 28px rgba(34,211,238,0.6), 0 0 56px rgba(34,211,238,0.2)' 
                : '0 0 14px rgba(34,211,238,0.3)',
              scale: expanded ? 1.05 : 1
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="w-11 h-11 rounded-full flex items-center justify-center relative z-10"
            style={{
              background: expanded 
                ? 'linear-gradient(135deg, rgba(34,211,238,0.25) 0%, rgba(6,182,212,0.12) 100%)'
                : 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(6,182,212,0.06) 100%)',
              border: `1.5px solid rgba(34,211,238,${expanded ? '0.6' : '0.3'})`,
            }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="rgba(34,211,238,0.95)" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </motion.div>
        </div>
        {/* Collapsed: just show truncated name */}
        {!expanded && (
          <motion.span 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-cyan-300/70 text-[12px] font-['Jost'] font-medium max-w-[140px] truncate"
          >
            {placeName}
          </motion.span>
        )}
      </div>

      {/* Expanded Tooltip */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0, scale: 0.92 }}
            animate={{ opacity: 1, height: 'auto', scale: 1 }}
            exit={{ opacity: 0, height: 0, scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            className="mt-2.5 rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(34,211,238,0.10) 0%, rgba(6,182,212,0.04) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(34,211,238,0.25)',
              boxShadow: '0 0 24px rgba(34,211,238,0.15), inset 0 1px 0 rgba(34,211,238,0.1)',
            }}
          >
            <div className="px-3.5 py-3">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.25)' }}>
                  <svg className="w-3.5 h-3.5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-['Jost'] font-semibold text-[13px] leading-tight">{placeName}</p>
                  {address && <p className="text-white/45 text-[11px] mt-0.5 font-['Jost']">{address}</p>}
                </div>
              </div>
              {comment && (
                <p className="text-cyan-300/50 text-[11px] italic mt-2 pl-8 font-['Jost']">{comment}</p>
              )}
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
                className="flex items-center gap-1.5 mt-2.5 pl-8"
              >
                <svg className="w-3 h-3 text-cyan-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="text-cyan-400/70 text-[10px] font-['Jost'] tracking-wide">Toque para abrir no Maps</span>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Time */}
      <div className={`flex items-center gap-1.5 mt-1.5 ${isOwn ? 'justify-end' : ''}`}>
        <p className="text-[10px] font-['Jost'] text-white/35">{formatTime(timestamp)}</p>
        {isOwn && (
          <svg className="w-3 h-3 text-blue-400/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </div>
  );
};

// Self-destruct countdown component
const SelfDestructCountdown = ({ expiresAt, messageId }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpiring, setIsExpiring] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setIsExpiring(true);
        // Call API to expire message
        axios.post(`${API}/chat/messages/${messageId}/expire`).catch(() => {});
        return '0s';
      }
      
      if (diff < 60000) { // Less than 1 minute
        return `${Math.ceil(diff / 1000)}s`;
      } else if (diff < 3600000) { // Less than 1 hour
        return `${Math.ceil(diff / 60000)}m`;
      } else {
        const hours = Math.floor(diff / 3600000);
        const mins = Math.ceil((diff % 3600000) / 60000);
        return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
      }
    };
    
    setTimeLeft(calculateTimeLeft());
    
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt, messageId, API]);
  
  if (isExpiring) return null;
  
  return (
    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-500/20">
      <svg className="w-2.5 h-2.5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-[9px] text-orange-400 font-medium font-['Jost']">{timeLeft}</span>
    </div>
  );
};

const ChatSection = ({ currentMember, initialOpenChat, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [notification, setNotification] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenTimestamp, setLastSeenTimestamp] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { msgId, x, y }
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showPhoneOptions, setShowPhoneOptions] = useState(false);
  const [phoneTooltipPosition, setPhoneTooltipPosition] = useState({ top: 0, left: 0 });
  const [typingPlaceholder, setTypingPlaceholder] = useState(''); // Typing effect placeholder
  const [encryptionEnabled, setEncryptionEnabled] = useState(false); // Encryption mode
  const [decryptModal, setDecryptModal] = useState(null); // { messageId, encryptedText }
  const [decryptPin, setDecryptPin] = useState(['', '', '', '']); // PIN as array for individual editing
  const [decryptedMessages, setDecryptedMessages] = useState({}); // { messageId: decryptedText }
  const [decryptStatus, setDecryptStatus] = useState(null); // null, 'success', 'error'
  const [activeDigitIndex, setActiveDigitIndex] = useState(0); // Which digit box is active
  const [replyingTo, setReplyingTo] = useState(null); // Message being replied to
  const [swipingMsgId, setSwipingMsgId] = useState(null); // Message currently being swiped
  const [swipeX, setSwipeX] = useState(0); // Current swipe offset
  const [highlightedMsgId, setHighlightedMsgId] = useState(null); // Message to highlight when clicking reply
  const [searchQuery, setSearchQuery] = useState(''); // Search query for members/messages
  const [showSearch, setShowSearch] = useState(false); // Show search bar
  const [showWIAModal, setShowWIAModal] = useState(false); // Show W IA quick access modal
  const [isTyping, setIsTyping] = useState(null); // Typing indicator { name, recipientId }
  const [wiaIntervention, setWiaIntervention] = useState(null); // W IA suggestion in chat { suggestion, timestamp }
  const [wiaInterventionTimer, setWiaInterventionTimer] = useState(null); // Timer for W IA to leave
  const [wiaConversationActive, setWiaConversationActive] = useState(false); // W IA conversation mode active
  const [wiaActivatedMsgId, setWiaActivatedMsgId] = useState(null); // ID of W message that activated conversation (for glow effect)
  const [showMentionList, setShowMentionList] = useState(false); // Show @ mention dropdown
  const [mentionQuery, setMentionQuery] = useState(''); // Current mention search query
  const [mentionStartIndex, setMentionStartIndex] = useState(null); // Position where @ was typed
  const [longPressMsg, setLongPressMsg] = useState(null); // Message being long-pressed for tooltip
  // Self-destruct message states
  const [showSelfDestructMenu, setShowSelfDestructMenu] = useState(false); // Show self-destruct time options
  const [selfDestructSeconds, setSelfDestructSeconds] = useState(null); // Selected self-destruct time
  const [sensitiveWarning, setSensitiveWarning] = useState(null); // { reason, suggestedTime } - IA detection warning
  // Real-time translation states
  const [translationEnabled, setTranslationEnabled] = useState(true); // Enable/disable translation
  const [userLanguage, setUserLanguage] = useState(null); // User's preferred language
  const [translations, setTranslations] = useState({}); // { messageId: { text, sourceLang } }
  const [showLanguageModal, setShowLanguageModal] = useState(false); // Show language selection modal
  const [supportedLanguages, setSupportedLanguages] = useState([]); // List of supported languages
  // Smart Chat Actions states
  const [detectedAction, setDetectedAction] = useState(null); // { action_type, confidence, details }
  const [showActionConfirm, setShowActionConfirm] = useState(false); // Show action confirmation modal
  const [actionLoading, setActionLoading] = useState(false); // Loading state for action execution
  const [reminders, setReminders] = useState([]); // User's reminders
  const [showRemindersModal, setShowRemindersModal] = useState(false); // Show reminders list
  const [showSummaryModal, setShowSummaryModal] = useState(false); // Show conversation summary
  const [conversationSummary, setConversationSummary] = useState(null); // Summary data
  const [transcriptions, setTranscriptions] = useState({}); // { messageId: transcription }
  const [transcribingIds, setTranscribingIds] = useState(new Set()); // Currently transcribing
  // @Tag Action Modals
  const [showWcashModal, setShowWcashModal] = useState(false); // @Wcash transfer modal
  const [showRecadoModal, setShowRecadoModal] = useState(false); // @Recado reminder modal
  const [showConversaModal, setShowConversaModal] = useState(false); // @Conversa summary modal
  const [wcashAmount, setWcashAmount] = useState(''); // Transfer amount
  const [wcashRecipient, setWcashRecipient] = useState(''); // Transfer recipient name
  const [recadoText, setRecadoText] = useState(''); // Reminder text
  const [recadoTime, setRecadoTime] = useState('1h');
  const [showPlusMenu, setShowPlusMenu] = useState(false); // + tooltip menu
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [locationComment, setLocationComment] = useState('');
  const [locationError, setLocationError] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({}); // { memberId: count }
  const [activeWAviso, setActiveWAviso] = useState(null); // Current W aviso being shown
  // Wchat entry intro — show once per browser session
  const [showIntro, setShowIntro] = useState(() => {
    try { return !sessionStorage.getItem('wchat_intro_seen'); } catch { return true; }
  });

  const cachedGpsRef = useRef(null); // Cache GPS position

  const getGpsFast = () => new Promise((resolve, reject) => {
    // Return cached if < 2 min old
    if (cachedGpsRef.current && Date.now() - cachedGpsRef.current.ts < 120000) {
      return resolve(cachedGpsRef.current.pos);
    }
    if (!navigator.geolocation) return reject(new Error('GPS não disponível'));
    // Fast fix first (low accuracy, instant)
    navigator.geolocation.getCurrentPosition(
      (pos) => { cachedGpsRef.current = { pos, ts: Date.now() }; resolve(pos); },
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 120000 }
    );
  });

  // Check for new W voice avisos periodically (ONLY voice, only recent, show once)
  const lastAvisoCheckRef = useRef(null);
  const dismissedAvisosRef = useRef(new Set());
  const shownAvisosRef = useRef(new Set()); // Track shown avisos to avoid re-showing
  useEffect(() => {
    if (!currentMember) return;
    const checkAvisos = async () => {
      try {
        const r = await axios.get(`${API}/w-avisos/${currentMember.id}?limit=3`);
        const avisos = (r.data?.avisos || [])
          .filter(a => a.message_type === 'voice' 
            && !dismissedAvisosRef.current.has(a.id)
            && !shownAvisosRef.current.has(a.id)
            // Only show avisos from last 5 minutes
            && (Date.now() - new Date(a.timestamp).getTime()) < 300000
          );
        
        if (avisos.length > 0 && !activeWAviso) {
          shownAvisosRef.current.add(avisos[0].id);
          setActiveWAviso(avisos[0]);
        }
      } catch (e) {}
    };
    checkAvisos();
    const interval = setInterval(checkAvisos, 15000);
    return () => clearInterval(interval);
  }, [currentMember]);

  const handleDeleteAviso = async (avisoId) => {
    try {
      if (!avisoId) return;
      dismissedAvisosRef.current.add(avisoId);
      await axios.delete(`${API}/chat/messages/${avisoId}`);
      setActiveWAviso(null);
      loadMessages();
    } catch (e) {
      console.error('[W Aviso Delete]', e);
      setActiveWAviso(null);
    }
  };

  const handleDismissAviso = () => {
    if (activeWAviso) dismissedAvisosRef.current.add(activeWAviso.id);
    setActiveWAviso(null);
  };

  const searchLocation = async (query) => {
    if (!query.trim()) return;
    setLocationSearching(true);
    setLocationError('');
    setLocationResults([]);
    try {
      const r = await fetch(`${API}/location/search?q=${encodeURIComponent(query)}`);
      const d = await r.json();
      setLocationResults(d.places || []);
      if ((d.places || []).length === 0) setLocationError('Nenhum resultado encontrado');
    } catch (err) {
      setLocationError('Erro na busca: ' + err.message);
    } finally {
      setLocationSearching(false);
    }
  };

  const loadNearby = async () => {
    setLocationSearching(true);
    setLocationError('');
    setLocationResults([]);
    try {
      const pos = await getGpsFast();
      const { latitude, longitude } = pos.coords;
      const r = await fetch(`${API}/location/nearby?lat=${latitude}&lon=${longitude}`);
      const d = await r.json();
      setLocationResults(d.places || []);
      if ((d.places || []).length === 0) setLocationError('Nenhum local próximo encontrado');
    } catch (err) {
      setLocationError(err.code === 1 ? 'Permissão de localização negada' : err.code === 3 ? 'GPS lento, tente novamente' : 'Erro: ' + err.message);
    } finally {
      setLocationSearching(false);
    }
  };

  const sendCurrentLocation = async () => {
    if (!currentMember) return;
    setLocationSearching(true);
    setLocationError('');
    try {
      const pos = await getGpsFast();
      const { latitude, longitude } = pos.coords;
      const revR = await fetch(`${API}/location/reverse?lat=${latitude}&lon=${longitude}`);
      const rev = await revR.json();
      const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}`;
      const msg = `📍 ${rev.name || 'Minha localização'}\n${rev.address || ''}\n${mapUrl}${locationComment.trim() ? '\n💬 ' + locationComment.trim() : ''}`;
      await sendChatMessage({ sender_id: currentMember.id, sender_name: currentMember.name, room: 'general', recipient_id: selectedRecipient?.id || null, message: msg, message_type: 'location' });
      loadMessages();
      setShowLocationModal(false); setLocationResults([]); setLocationSearch(''); setLocationComment(''); setLocationError('');
    } catch (err) {
      setLocationError(err.code === 1 ? 'Permissão de localização negada' : 'Erro: ' + err.message);
    } finally {
      setLocationSearching(false);
    }
  };

  // Auto-load nearby places when modal opens
  useEffect(() => {
    if (showLocationModal && locationResults.length === 0 && !locationSearching) {
      loadNearby();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLocationModal]);
  const pinInputRefs = useRef([null, null, null, null]);
  const phoneButtonRef = useRef(null);
  const swipeStartX = useRef(0);
  const typingTimeoutRef = useRef(null);
  const wiaInterventionRef = useRef(null); // Ref to track intervention timeout
  const inputRef = useRef(null); // Ref for input element
  const lastWiaInterventionTime = useRef(0); // Cooldown to prevent spam
  const longPressTimerRef = useRef(null); // Timer for long press detection
  const selectedRecipientRef = useRef(null); // Ref for current selected recipient (for closure access)

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isAtBottomRef = useRef(true);
  const currentAudioRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const incomingPollRef = useRef(null);

  const showNotif = useCallback((msg, type = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // Scroll to and highlight a message when clicking on reply reference
  const scrollToMessage = useCallback((messageId) => {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMsgId(messageId);
      // Remove highlight after animation
      setTimeout(() => setHighlightedMsgId(null), 2000);
    }
  }, []);

  // Filter members based on search query - include ALL members for selection
  const filteredMembers = allMembers.filter(m => 
    m.id !== currentMember?.id && 
    (searchQuery === '' || m.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // All members except current for bottom sheet selection (shows everyone)
  const selectableMembers = allMembers;

  // Fetch unread counts when selector opens
  useEffect(() => {
    if ((showRecipientSelector || showSearch) && currentMember) {
      const fetchUnread = async () => {
        try {
          const r = await axios.get(`${API}/chat/unread-counts/${currentMember.id}`);
          setUnreadCounts(r.data || {});
        } catch (e) {}
      };
      fetchUnread();
    }
  }, [showRecipientSelector, showSearch, currentMember]);

  // Typing effect for placeholder text
  useEffect(() => {
    const phrases = [
      'Mensagem...',
      'Wchat digitando...',
      'Escreva aqui...',
    ];
    let phraseIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingTimer;
    
    const typeEffect = () => {
      const currentPhrase = phrases[phraseIndex];
      
      if (!isDeleting) {
        setTypingPlaceholder(currentPhrase.substring(0, charIndex + 1));
        charIndex++;
        
        if (charIndex === currentPhrase.length) {
          isDeleting = true;
          typingTimer = setTimeout(typeEffect, 2500);
          return;
        }
        typingTimer = setTimeout(typeEffect, 80);
      } else {
        setTypingPlaceholder(currentPhrase.substring(0, charIndex - 1));
        charIndex--;
        
        if (charIndex === 0) {
          isDeleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
          typingTimer = setTimeout(typeEffect, 500);
          return;
        }
        typingTimer = setTimeout(typeEffect, 40);
      }
    };
    
    typingTimer = setTimeout(typeEffect, 500);
    
    return () => {
      if (typingTimer) clearTimeout(typingTimer);
    };
  }, []);

  // Encryption/Decryption functions
  const encryptMessage = (text, pin) => {
    // Use the 4-digit PIN as the key
    const encrypted = CryptoJS.AES.encrypt(text, pin).toString();
    return encrypted;
  };

  const decryptMessage = (encryptedText, pin) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedText, pin);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      if (!decrypted) return null; // Wrong PIN
      return decrypted;
    } catch (e) {
      return null; // Decryption failed
    }
  };

  // Get last 4 digits of member's card number (using member ID as fallback)
  const getMemberPin = (member) => {
    // Use last 4 chars of member ID as PIN
    return member?.id?.slice(-4) || '0000';
  };

  // Detect sensitive content in message - debounced
  useEffect(() => {
    if (!input.trim() || input.length < 8) {
      setSensitiveWarning(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/chat/detect-sensitive`, { message: input });
        if (res.data.is_sensitive && !selfDestructSeconds) {
          setSensitiveWarning({
            reason: res.data.reason,
            suggestedTime: res.data.suggested_expiration
          });
        } else {
          setSensitiveWarning(null);
        }
      } catch (e) {
        // Ignore errors
      }
    }, 800);
    
    return () => clearTimeout(timer);
  }, [input, selfDestructSeconds]);

  // Handle decrypt modal
  const handleDecryptAttempt = () => {
    const pinString = decryptPin.join('');
    if (!decryptModal || pinString.length !== 4) return;
    
    const decrypted = decryptMessage(decryptModal.encryptedText, pinString);
    if (decrypted) {
      // Success - show check icon then close
      setDecryptStatus('success');
      setTimeout(() => {
        setDecryptedMessages(prev => ({
          ...prev,
          [decryptModal.messageId]: decrypted
        }));
        setDecryptModal(null);
        setDecryptPin(['', '', '', '']);
        setDecryptStatus(null);
        setActiveDigitIndex(0);
      }, 800);
    } else {
      // Wrong PIN - show X icon then reset
      setDecryptStatus('error');
      setTimeout(() => {
        setDecryptPin(['', '', '', '']);
        setDecryptStatus(null);
        setActiveDigitIndex(0);
      }, 800);
    }
  };

  // Handle opening specific chat from notification or URL param
  useEffect(() => {
    if (initialOpenChat && allMembers.length > 0) {
      const member = allMembers.find(m => m.id === initialOpenChat);
      if (member) {
        setSelectedRecipient(member);
      }
    }
  }, [initialOpenChat, allMembers]);

  // Listen for service worker messages (notification clicks and incoming calls)
  useEffect(() => {
    const handleSWMessage = async (event) => {
      console.log('[Chat] SW message received:', event.data?.type);
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.sender_id) {
        // Find the sender and open their chat
        const sender = allMembers.find(m => m.id === event.data.sender_id);
        if (sender) {
          setSelectedRecipient(sender);
        }
      }
      // Handle incoming call notification from push
      if (event.data?.type === 'INCOMING_CALL' && currentMember && !activeCall) {
        console.log('[Chat] Incoming call notification received, checking API...');
        try {
          const { data } = await axios.get(`${API}/call/incoming/${currentMember.id}`);
          if (data?.id && data?.offer) {
            console.log('[Chat] Found incoming call via push notification:', data.id);
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
            setActiveCall({ id: data.id, callerName: data.caller_name, calleeName: currentMember.name, isCaller: false });
          }
        } catch (e) {
          console.log('[Chat] Error checking call after push:', e.message);
        }
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, [allMembers, currentMember, activeCall]);

  // Clear badge when viewing chat
  useEffect(() => {
    clearAppBadge();
    setLastSeenTimestamp(new Date().toISOString());
  }, [selectedRecipient]);

  // Clear badge on mount and on app focus (iOS PWA badge fix)
  useEffect(() => {
    clearAppBadge();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearAppBadge();
        setUnreadCount(0);
      }
    };
    const handleFocus = () => { clearAppBadge(); setUnreadCount(0); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') setPushEnabled(true);
  }, []);

  const enablePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { showNotif('Instale o app para notificacoes.', 'warn'); return; }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { showNotif('Permissao negada.', 'warn'); return; }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await axios.get(`${API}/push/vapid-key`);
      const vapidKey = keyRes.data.key;
      if (!vapidKey) { setPushEnabled(true); return; }
      const u = (b) => { const p = '='.repeat((4 - b.length % 4) % 4); const d = (b + p).replace(/-/g, '+').replace(/_/g, '/'); const r = window.atob(d); return Uint8Array.from([...r].map(c => c.charCodeAt(0))); };
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: u(vapidKey) });
      const j = sub.toJSON();
      j.member_id = currentMember?.id || null;
      await axios.post(`${API}/push/subscribe`, j);
      setPushEnabled(true);
      showNotif('Notificacoes ativadas!', 'success');
    } catch (err) {
      if ('Notification' in window && Notification.permission === 'granted') setPushEnabled(true);
    } finally { setPushLoading(false); }
  };

  const disablePushNotifications = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await sub.unsubscribe(); await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }); }
      setPushEnabled(false);
      showNotif('Desativadas', 'info');
    } catch (err) {}
  };

  const scrollToBottom = useCallback(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, []);
  useEffect(() => { if (isAtBottomRef.current) scrollToBottom(); }, [messages, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    isAtBottomRef.current = c.scrollHeight - c.scrollTop - c.clientHeight < 80;
    setShowScrollBtn(!isAtBottomRef.current);
  }, []);

  const loadMembers = async () => { try { const r = await getMembers(); setAllMembers(r.data); } catch (e) {} };

  // Force refresh members from API on mount and listen for updates
  useEffect(() => {
    // Refresh from API to ensure fresh data
    const initialLoad = async () => {
      try {
        const r = await refreshMembers();
        setAllMembers(r.data);
      } catch (e) {
        // Fallback to cached
        loadMembers();
      }
    };
    initialLoad();
    
    // Listen for member cache updates
    const unsubscribe = onCacheUpdate('members', loadMembers);
    return () => unsubscribe();
  }, []);

  const loadMessages = async () => {
    try {
      const r = await getChatMessages('general');
      let filtered = r.data;
      if (selectedRecipient && currentMember) {
        const isWChannel = selectedRecipient.id === 'w-ia';
        
        if (isWChannel) {
          // W IA channel - show only W messages targeted to current member
          const wDmRoom = `dm_${[currentMember.id, 'w-ia'].sort().join('_')}`;
          filtered = r.data.filter(msg =>
            (msg.sender_id === 'w-ia' && (
              msg.recipient_id === currentMember.id ||
              msg.room === wDmRoom
            )) ||
            (msg.sender_id === currentMember.id && msg.recipient_id === 'w-ia')
          );
        } else {
          // Regular DM - show messages between the two members, NO W IA messages
          filtered = r.data.filter(msg =>
            (msg.sender_id === currentMember.id && msg.recipient_id === selectedRecipient.id) ||
            (msg.sender_id === selectedRecipient.id && msg.recipient_id === currentMember.id)
          );
        }
      } else {
        // General chat - show only messages without recipient_id
        // Exclude W IA messages that are targeted to specific DMs
        filtered = r.data.filter(msg => 
          !msg.recipient_id
        );
      }
      // Limit to last 30 messages for better performance
      const limitedMessages = filtered.slice(-30);
      setMessages(limitedMessages);
      
      // Calculate unread messages - reset when viewing
      if (document.visibilityState === 'visible') {
        setUnreadCount(0);
        clearAppBadge();
      } else if (lastSeenTimestamp && filtered.length > 0 && currentMember) {
        const newMsgs = filtered.filter(msg => 
          msg.sender_id !== currentMember.id && 
          new Date(msg.timestamp) > new Date(lastSeenTimestamp)
        );
        setUnreadCount(Math.min(newMsgs.length, 50));
      }
    } catch (e) {}
  };

  useEffect(() => { loadMembers(); loadMessages(); }, [currentMember]);
  useEffect(() => { loadMessages(); }, [selectedRecipient]);
  useEffect(() => { const t = setInterval(loadMessages, 1500); return () => clearInterval(t); }, [currentMember, selectedRecipient]);
  
  // Keep ref updated for closures
  useEffect(() => { selectedRecipientRef.current = selectedRecipient; }, [selectedRecipient]);

  // Translation - Detect user's language and load supported languages
  useEffect(() => {
    const detectAndSetLanguage = async () => {
      // Check if user has a saved preference
      if (currentMember?.preferred_language) {
        setUserLanguage(currentMember.preferred_language);
        return;
      }
      
      // Detect from browser
      const browserLang = navigator.language?.split('-')[0] || 'pt';
      setUserLanguage(browserLang);
      
      // Save to member profile
      if (currentMember?.id) {
        try {
          await axios.put(`${API}/members/${currentMember.id}/language`, {
            preferred_language: browserLang
          });
        } catch (e) {
          console.log('[Translation] Could not save language preference');
        }
      }
    };
    
    const loadSupportedLanguages = async () => {
      try {
        const { data } = await axios.get(`${API}/translation/languages`);
        if (data?.languages) {
          setSupportedLanguages(data.languages);
        }
      } catch (e) {
        // Fallback to common languages
        setSupportedLanguages([
          { code: 'pt', name: 'Português' },
          { code: 'en', name: 'English' },
          { code: 'es', name: 'Español' },
          { code: 'fr', name: 'Français' },
          { code: 'it', name: 'Italiano' },
          { code: 'de', name: 'Deutsch' },
        ]);
      }
    };
    
    if (currentMember) {
      detectAndSetLanguage();
      loadSupportedLanguages();
    }
  }, [currentMember]);

  // Translation function - translate a single message
  const translatingIdsRef = useRef(new Set());
  const translatedIdsRef = useRef(new Set()); // Track all attempted translations
  
  const translateMessage = useCallback(async (messageId, text, targetLanguage) => {
    if (!text || text.length < 3) return;
    
    // Check refs to avoid duplicate calls
    if (translatingIdsRef.current.has(messageId) || translatedIdsRef.current.has(messageId)) return;
    
    translatingIdsRef.current.add(messageId);
    
    try {
      const { data } = await axios.post(`${API}/chat/translate`, {
        text,
        target_language: targetLanguage
      });
      
      // Mark as translated regardless of result
      translatedIdsRef.current.add(messageId);
      
      if (data?.translated_text && data.translated_text !== text) {
        setTranslations(prev => ({
          ...prev,
          [messageId]: {
            text: data.translated_text,
            sourceLang: data.source_language,
            targetLang: data.target_language
          }
        }));
      } else {
        // Mark as "no translation needed" to prevent retrying
        setTranslations(prev => ({
          ...prev,
          [messageId]: { text: text, sourceLang: targetLanguage, targetLang: targetLanguage, sameLanguage: true }
        }));
      }
    } catch (e) {
      console.log('[Translation] Error translating message:', e);
      // Mark as attempted to prevent infinite retries
      translatedIdsRef.current.add(messageId);
      setTranslations(prev => ({
        ...prev,
        [messageId]: { text: text, failed: true }
      }));
    } finally {
      translatingIdsRef.current.delete(messageId);
    }
  }, []); // No dependencies to avoid loops

  // Auto-translate new messages when they arrive
  useEffect(() => {
    if (!translationEnabled || !userLanguage || !messages.length) return;
    
    // Find ALL text messages that need translation (including own messages)
    const messagesToTranslate = messages.filter(msg => 
      msg.message_type === 'text' &&
      msg.message &&
      msg.message.length >= 3 &&
      !translatedIdsRef.current.has(msg.id) &&
      !translatingIdsRef.current.has(msg.id) &&
      !msg.is_expired
    );
    
    // Translate up to 5 messages at a time (most recent)
    const recentToTranslate = messagesToTranslate.slice(-5);
    
    recentToTranslate.forEach(msg => {
      translateMessage(msg.id, msg.message, userLanguage);
    });
  }, [messages, translationEnabled, userLanguage, translateMessage]);

  // Change user's preferred language
  const changeUserLanguage = useCallback(async (langCode) => {
    setUserLanguage(langCode);
    setShowLanguageModal(false);
    
    // Clear existing translations and refs to re-translate
    setTranslations({});
    translatedIdsRef.current.clear();
    translatingIdsRef.current.clear();
    
    // Save to member profile
    if (currentMember?.id) {
      try {
        await axios.put(`${API}/members/${currentMember.id}/language`, {
          preferred_language: langCode
        });
        showNotif(`Idioma alterado para ${supportedLanguages.find(l => l.code === langCode)?.name || langCode}`, 'success');
      } catch (e) {
        console.log('[Translation] Could not save language preference');
      }
    }
  }, [currentMember?.id, supportedLanguages, showNotif]);

  // ===== @TAG ACTIONS =====
  
  // Detect @tags in input text and open corresponding modal
  const lastDetectedTagRef = useRef('');
  
  const detectTagAction = useCallback((text) => {
    if (!text) {
      lastDetectedTagRef.current = '';
      return;
    }
    
    const lowerText = text.toLowerCase();
    
    // Prevent triggering same tag twice
    if (lowerText.includes('@wcash') && lastDetectedTagRef.current !== '@wcash') {
      lastDetectedTagRef.current = '@wcash';
      setShowWcashModal(true);
      // Clear the tag from input after a small delay
      setTimeout(() => setInput(prev => prev.replace(/@wcash/gi, '').trim()), 100);
    } else if (lowerText.includes('@recado') && lastDetectedTagRef.current !== '@recado') {
      lastDetectedTagRef.current = '@recado';
      setShowRecadoModal(true);
      setTimeout(() => setInput(prev => prev.replace(/@recado/gi, '').trim()), 100);
    } else if (lowerText.includes('@conversa') && lastDetectedTagRef.current !== '@conversa') {
      lastDetectedTagRef.current = '@conversa';
      setShowConversaModal(true);
      setTimeout(() => setInput(prev => prev.replace(/@conversa/gi, '').trim()), 100);
    } else if (!lowerText.includes('@wcash') && !lowerText.includes('@recado') && !lowerText.includes('@conversa')) {
      lastDetectedTagRef.current = '';
    }
  }, []);

  // Execute @Wcash transfer
  const executeWcashTransfer = useCallback(async () => {
    if (!wcashAmount || !wcashRecipient || !currentMember?.id) {
      showNotif('Preencha todos os campos', 'error');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const amount = parseFloat(wcashAmount);
      if (isNaN(amount) || amount <= 0) {
        showNotif('Valor inválido', 'error');
        setActionLoading(false);
        return;
      }
      
      const { data } = await axios.post(`${API}/chat/execute-transfer`, {
        sender_id: currentMember.id,
        recipient_name: wcashRecipient,
        amount: amount
      });
      
      if (data.success) {
        // Send receipt message to recipient
        const receiptMessage = `💸 **COMPROVANTE DE TRANSFERÊNCIA**\n\n` +
          `De: ${currentMember.name}\n` +
          `Valor: W$ ${amount.toFixed(2)}\n` +
          `Data: ${new Date().toLocaleString('pt-BR')}\n\n` +
          `✅ Transferência realizada com sucesso!`;
        
        // Send as a chat message to the recipient
        try {
          await axios.post(`${API}/chat/messages`, {
            sender_id: currentMember.id,
            sender_name: currentMember.name,
            message: receiptMessage,
            message_type: 'text',
            room: 'general',
            recipient_id: data.recipient_id
          });
        } catch (e) {
          console.log('[Wcash] Receipt send error:', e);
        }
        
        showNotif(`✅ W$ ${amount.toFixed(2)} enviados para ${data.recipient}! Novo saldo: W$ ${data.new_balance?.toFixed(2)}`, 'success');
        setShowWcashModal(false);
        setWcashAmount('');
        setWcashRecipient('');
      } else {
        showNotif(`❌ ${data.error}`, 'error');
      }
    } catch (e) {
      console.log('[Wcash] Transfer error:', e);
      showNotif('Erro ao transferir', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [wcashAmount, wcashRecipient, currentMember, showNotif]);

  // Execute @Recado reminder
  const executeRecado = useCallback(async () => {
    if (!recadoText || !currentMember?.id) {
      showNotif('Digite o texto do recado', 'error');
      return;
    }
    
    setActionLoading(true);
    
    try {
      // Parse reminder time
      let remindAt = new Date();
      const lowerTime = recadoTime.toLowerCase();
      
      if (lowerTime === '1h') {
        remindAt.setTime(remindAt.getTime() + 60 * 60 * 1000);
      } else if (lowerTime === '2h') {
        remindAt.setTime(remindAt.getTime() + 2 * 60 * 60 * 1000);
      } else if (lowerTime === '6h') {
        remindAt.setTime(remindAt.getTime() + 6 * 60 * 60 * 1000);
      } else if (lowerTime === 'amanhã' || lowerTime === 'amanha') {
        remindAt.setDate(remindAt.getDate() + 1);
        remindAt.setHours(9, 0, 0);
      } else if (lowerTime === '1 semana') {
        remindAt.setDate(remindAt.getDate() + 7);
      }
      
      const { data } = await axios.post(`${API}/chat/reminders`, {
        member_id: currentMember.id,
        message: recadoText,
        remind_at: remindAt.toISOString()
      });
      
      if (data.success) {
        showNotif(`⏰ Recado criado para ${remindAt.toLocaleString('pt-BR')}`, 'success');
        setShowRecadoModal(false);
        setRecadoText('');
        setRecadoTime('1h');
      }
    } catch (e) {
      console.log('[Recado] Error:', e);
      showNotif('Erro ao criar recado', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [recadoText, recadoTime, currentMember?.id, showNotif]);

  // Execute @Conversa summary
  const executeConversa = useCallback(async () => {
    if (!currentMember?.id) return;
    
    setActionLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/chat/summarize`, {
        member_id: currentMember.id,
        recipient_id: selectedRecipient?.id,
        limit: 50
      });
      
      setConversationSummary(data);
      setShowConversaModal(false);
      setShowSummaryModal(true);
    } catch (e) {
      console.log('[Conversa] Error:', e);
      showNotif('Erro ao gerar resumo', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [currentMember?.id, selectedRecipient?.id, showNotif]);

  // Detect @tags when input changes
  useEffect(() => {
    detectTagAction(input);
  }, [input, detectTagAction]);

  // Legacy execute action (keeping for backwards compatibility)
  const executeAction = useCallback(async () => {
    if (!detectedAction || !currentMember?.id) return;
    
    setActionLoading(true);
    
    try {
      if (detectedAction.action_type === 'reminder') {
        // Create reminder
        const { task, when } = detectedAction.details || {};
        
        // Parse relative time to absolute
        let remindAt = new Date();
        const lowerWhen = (when || '').toLowerCase();
        
        if (lowerWhen.includes('amanhã')) {
          remindAt.setDate(remindAt.getDate() + 1);
          // Extract time if mentioned
          const timeMatch = when.match(/(\d{1,2})[h:]?(\d{2})?/);
          if (timeMatch) {
            remindAt.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2] || '0'), 0);
          } else {
            remindAt.setHours(9, 0, 0); // Default 9am
          }
        } else if (lowerWhen.includes('hora')) {
          const hoursMatch = when.match(/(\d+)/);
          const hours = hoursMatch ? parseInt(hoursMatch[1]) : 1;
          remindAt.setTime(remindAt.getTime() + hours * 60 * 60 * 1000);
        } else if (lowerWhen.includes('minuto')) {
          const minsMatch = when.match(/(\d+)/);
          const mins = minsMatch ? parseInt(minsMatch[1]) : 30;
          remindAt.setTime(remindAt.getTime() + mins * 60 * 1000);
        } else {
          remindAt.setTime(remindAt.getTime() + 60 * 60 * 1000); // Default 1 hour
        }
        
        const { data } = await axios.post(`${API}/chat/reminders`, {
          member_id: currentMember.id,
          message: task || input,
          remind_at: remindAt.toISOString()
        });
        
        if (data.success) {
          showNotif(`⏰ Lembrete criado para ${remindAt.toLocaleString('pt-BR')}`, 'success');
          setDetectedAction(null);
          setShowActionConfirm(false);
        }
      } else if (detectedAction.action_type === 'transfer') {
        // Execute transfer
        const { amount, recipient_name } = detectedAction.details || {};
        
        const { data } = await axios.post(`${API}/chat/execute-transfer`, {
          sender_id: currentMember.id,
          recipient_name: recipient_name,
          amount: parseFloat(amount) || 0
        });
        
        if (data.success) {
          showNotif(`✅ W$ ${amount} transferidos para ${data.recipient}! Saldo: W$ ${data.new_balance?.toFixed(2)}`, 'success');
          setDetectedAction(null);
          setShowActionConfirm(false);
        } else {
          showNotif(`❌ ${data.error}`, 'error');
        }
      } else if (detectedAction.action_type === 'summary') {
        // Generate summary
        const { data } = await axios.post(`${API}/chat/summarize`, {
          member_id: currentMember.id,
          recipient_id: selectedRecipient?.id,
          limit: 50
        });
        
        setConversationSummary(data);
        setShowSummaryModal(true);
        setDetectedAction(null);
        setShowActionConfirm(false);
      }
    } catch (e) {
      console.log('[Actions] Execution error:', e);
      showNotif('Erro ao executar ação', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [detectedAction, currentMember?.id, input, selectedRecipient?.id, showNotif]);

  // Transcribe voice message
  const transcribeVoiceMessage = useCallback(async (messageId) => {
    if (transcribingIds.has(messageId) || transcriptions[messageId]) return;
    
    setTranscribingIds(prev => new Set([...prev, messageId]));
    
    try {
      const { data } = await axios.post(`${API}/voice/transcribe-message`, {
        message_id: messageId
      });
      
      if (data.success && data.transcription) {
        setTranscriptions(prev => ({
          ...prev,
          [messageId]: data.transcription
        }));
      }
    } catch (e) {
      console.log('[Transcribe] Error:', e);
    } finally {
      setTranscribingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  }, [transcribingIds, transcriptions]);

  // Load user's reminders
  const loadReminders = useCallback(async () => {
    if (!currentMember?.id) return;
    
    try {
      const { data } = await axios.get(`${API}/chat/reminders/${currentMember.id}`);
      if (data?.reminders) {
        setReminders(data.reminders);
      }
    } catch (e) {
      console.log('[Reminders] Load error:', e);
    }
  }, [currentMember?.id]);

  // Complete a reminder
  const completeReminder = useCallback(async (reminderId) => {
    try {
      await axios.put(`${API}/chat/reminders/${reminderId}/complete`);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      showNotif('Lembrete concluído!', 'success');
    } catch (e) {
      console.log('[Reminders] Complete error:', e);
    }
  }, [showNotif]);

  // Delete a reminder
  const deleteReminder = useCallback(async (reminderId) => {
    try {
      await axios.delete(`${API}/chat/reminders/${reminderId}`);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (e) {
      console.log('[Reminders] Delete error:', e);
    }
  }, []);

  // Load reminders on mount
  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const sendTypingStatus = useCallback(async () => {
    if (!currentMember || !selectedRecipient) return;
    try {
      await axios.post(`${API}/chat/typing`, {
        sender_id: currentMember.id,
        sender_name: currentMember.name,
        recipient_id: selectedRecipient.id
      });
    } catch (e) {}
  }, [currentMember, selectedRecipient]);

  // Poll for typing status from others
  useEffect(() => {
    if (!currentMember || !selectedRecipient) {
      setIsTyping(null);
      return;
    }
    
    const checkTyping = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/typing/${currentMember.id}/${selectedRecipient.id}`);
        if (data?.is_typing && data?.sender_name) {
          setIsTyping({ name: data.sender_name, recipientId: data.sender_id });
        } else {
          setIsTyping(null);
        }
      } catch (e) {
        setIsTyping(null);
      }
    };
    
    const interval = setInterval(checkTyping, 2000);
    return () => clearInterval(interval);
  }, [currentMember, selectedRecipient]);

  // W IA Contextual Intervention - analyzes conversation and makes relevant suggestions
  const fallbackInterventions = [
    "Senhores, posso ajudar com alguma recomendação especial?",
    "Novidades chegaram na adega. Interessados?",
    "Que tal agendar uma degustação privativa esta semana?",
    "Temos uma seleção especial de charutos cubanos disponível.",
    "Alguém gostaria de conhecer nosso novo single malt?",
    "Posso sugerir uma harmonização perfeita para o momento?",
    "Lembrando: evento exclusivo acontecendo em breve.",
    "Chegou um Bordeaux excepcional. Reservo para vocês?",
    "A temperatura está perfeita para um bom charuto hoje.",
    "Alguma preferência especial para esta noite?",
  ];

  // Fetch contextual intervention from backend
  const fetchWInterventionFromAI = async () => {
    try {
      // Prepare recent text messages for context
      const recentTextMessages = messages
        .filter(m => m.message_type === 'text' && m.message)
        .slice(-5)
        .map(m => ({
          sender_name: m.sender_name,
          message: m.message,
          message_type: m.message_type
        }));
      
      if (recentTextMessages.length < 1) {
        return fallbackInterventions[Math.floor(Math.random() * fallbackInterventions.length)];
      }
      
      const response = await axios.post(`${API}/chat/w-intervention`, {
        recent_messages: recentTextMessages,
        member_name: currentMember?.name || 'Membro'
      });
      
      return response.data.intervention || fallbackInterventions[Math.floor(Math.random() * fallbackInterventions.length)];
    } catch (e) {
      return fallbackInterventions[Math.floor(Math.random() * fallbackInterventions.length)];
    }
  };

  // W IA intervention - triggered by TAGS or @W mention
  // W responds DIRECTLY in chat (no popup needed)
  // Click on W's message to activate continuous conversation
  useEffect(() => {
    if (!currentMember || messages.length < 2) {
      return;
    }
    
    // Cooldown: don't trigger if W intervened in the last 5 seconds
    const now = Date.now();
    if (now - lastWiaInterventionTime.current < 5000) {
      return;
    }
    
    // Get the last message
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || !lastMsg.message) return;
    
    // IMPORTANT: Don't trigger on W's own messages
    if (lastMsg.sender_id === 'w-ia') {
      return;
    }
    
    // Check if the last message contains trigger tags or @W mention
    const hasTags = containsTriggerTags(lastMsg.message);
    const hasWMention = lastMsg.message?.toLowerCase().includes('@w');
    const shouldTrigger = hasTags || hasWMention || wiaConversationActive;
    
    if (!shouldTrigger) return;
    
    // CAPTURE the recipient context NOW, before setTimeout
    const capturedRecipient = selectedRecipient;
    const isInDM = capturedRecipient && capturedRecipient.id !== 'all' && capturedRecipient.id !== 'w-ia';
    // W IA always sends to the W-member channel, not the DM between two members
    const wTargetRoom = isInDM ? `dm_${['w-ia', currentMember.id].sort().join('_')}` : 'general';
    const wTargetRecipient = isInDM ? currentMember.id : null;
    
    // Clear any existing timer
    if (wiaInterventionRef.current) {
      clearTimeout(wiaInterventionRef.current);
    }
    
    // Fast response - send W message DIRECTLY to chat
    wiaInterventionRef.current = setTimeout(async () => {
      if (Date.now() - lastWiaInterventionTime.current < 5000) return;
      lastWiaInterventionTime.current = Date.now();
      
      try {
        const intervention = await fetchWInterventionFromAI();
        
        // Send W's message using the captured context
        // For DMs: send to W-member channel so it's private
        await axios.post(`${API}/chat/messages`, {
          sender_id: 'w-ia',
          sender_name: 'W',
          message: intervention,
          message_type: 'text',
          room: wTargetRoom,
          recipient_id: wTargetRecipient
        });
        
        loadMessages();
        scrollToBottom();
      } catch (e) {
        console.error('[W IA] Error:', e);
      }
    }, 500); // Fast 500ms response
    
    return () => {
      if (wiaInterventionRef.current) {
        clearTimeout(wiaInterventionRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, messages[messages.length - 1]?.id, currentMember, wiaConversationActive, selectedRecipient]);

  // Activate W IA conversation - called when user clicks on W's message
  const activateWiaConversation = (msgId) => {
    if (wiaConversationActive) return; // Already active
    
    setWiaConversationActive(true);
    setWiaActivatedMsgId(msgId);
    
    // Clear activation glow after 2 seconds
    setTimeout(() => {
      setWiaActivatedMsgId(null);
    }, 2000);
    
    // Focus on input
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  
  // Stop W IA conversation - called from long press menu
  const stopWiaConversation = () => {
    setWiaConversationActive(false);
    setWiaActivatedMsgId(null);
    setLongPressMsg(null);
  };

  // Send message to W IA and get response ONLY in W-member channel
  const sendMessageToWIA = async (userMessage) => {
    try {
      setLoading(true);
      
      // W always communicates in the W-member private channel
      const isWChannel = selectedRecipient?.id === 'w-ia';
      const wDmRoom = `dm_${['w-ia', currentMember.id].sort().join('_')}`;
      const currentRoom = isWChannel ? wDmRoom : (selectedRecipient ? `dm_${[currentMember.id, selectedRecipient.id].sort().join('_')}` : 'general');
      
      // W response always goes to W-member channel
      const wResponseRoom = wDmRoom;
      
      console.log('[W IA] Conversation context:', isWChannel ? 'W Channel' : 'Other', '| Room:', currentRoom);
      
      // Send user's message to current conversation
      const userMsgResult = await axios.post(`${API}/chat/messages`, {
        sender_id: currentMember.id,
        sender_name: currentMember.name,
        message: userMessage,
        message_type: 'text',
        room: currentRoom,
        recipient_id: selectedRecipient?.id || null
      });
      console.log('[W IA] User message sent:', userMsgResult.data?.id);
      
      // Reload to show user message
      loadMessages();
      scrollToBottom();
      
      // Get W's contextual response
      const recentMsgs = [...messages.slice(-4), { sender_name: currentMember.name, message: userMessage, message_type: 'text' }];
      
      const response = await axios.post(`${API}/chat/w-intervention`, {
        recent_messages: recentMsgs.map(m => ({
          sender_name: m.sender_name,
          message: m.message,
          message_type: m.message_type
        })),
        member_name: currentMember.name
      });
      
      const wResponse = response.data.intervention;
      console.log('[W IA] Got response:', wResponse?.slice(0, 50));
      
      // Send W's response to W-member private channel
      const wMsgResult = await axios.post(`${API}/chat/messages`, {
        sender_id: 'w-ia',
        sender_name: 'W',
        message: wResponse,
        message_type: 'text',
        room: wResponseRoom,
        recipient_id: currentMember.id,
        reply_to_id: null,
        reply_to_name: currentMember.name,
        reply_to_preview: userMessage.slice(0, 50)
      });
      console.log('[W IA] W message sent to:', isInDM ? 'DM' : 'Group', '| ID:', wMsgResult.data?.id);
      
      // Final reload and scroll immediately
      loadMessages();
      scrollToBottom();
      
    } catch (e) {
      console.error('[W IA] Error:', e);
      showNotif('Erro ao conversar com W', 'warn');
    } finally {
      setLoading(false);
    }
  };

  // Tags/Keywords that trigger W IA intervention
  const W_TRIGGER_TAGS = [
    'charuto', 'charutos', 'cigarro', 'cubano', 'cubanos',
    'vinho', 'vinhos', 'champanhe', 'champagne', 'espumante',
    'whisky', 'whiskey', 'bourbon', 'scotch', 'rum',
    'luxo', 'premium', 'exclusivo', 'raro',
    'roteiro', 'roteiros', 'viagem', 'viagens', 'destino',
    'relógio', 'relógios', 'rolex', 'patek', 'audemars',
    'bolsa', 'bolsas', 'hermes', 'louis vuitton', 'chanel',
    'degustação', 'harmonização', 'sommelier',
    'cuba', 'nicarágua', 'nicaragua', 'dominicana',
    'cohiba', 'montecristo', 'partagas', 'bolivar',
    'bordeaux', 'borgonha', 'champagne', 'napa',
    'evento', 'eventos', 'festa', 'celebração'
  ];

  // Check if message contains trigger tags
  const containsTriggerTags = (message) => {
    if (!message) return false;
    const lowerMsg = message.toLowerCase();
    return W_TRIGGER_TAGS.some(tag => lowerMsg.includes(tag));
  };

  // Render message text with highlighted tags (blue) and @W (violet, clickable)
  const renderMessageText = useCallback((text, msgType) => {
    if (!text) return null;
    
    // For location messages, render with clickable map link
    if (msgType === 'location') {
      const lines = text.split('\n');
      return lines.map((line, i) => {
        const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
        if (urlMatch) {
          return <a key={i} href={urlMatch[1]} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline text-xs block mt-1" onClick={(e) => e.stopPropagation()}>Abrir no Maps</a>;
        }
        if (line.startsWith('📍')) return <span key={i} className="font-semibold block">{line}</span>;
        if (line.startsWith('💬')) return <span key={i} className="text-white/60 italic block mt-1">{line}</span>;
        return <span key={i} className="block text-white/50">{line}</span>;
      });
    }
    
    // Split by URLs first
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const textWithUrls = text.split(urlRegex);
    
    const rendered = textWithUrls.map((segment, si) => {
      if (segment.match(urlRegex)) {
        return <a key={`u${si}`} href={segment} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline break-all" onClick={(e) => e.stopPropagation()}>{segment.length > 40 ? segment.substring(0, 40) + '...' : segment}</a>;
      }
      
      // Process tags and @W within text segments
      const tagPattern = W_TRIGGER_TAGS.map(tag => tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
      const combinedPattern = new RegExp(`(@W|${tagPattern})`, 'gi');
      const parts = segment.split(combinedPattern);
      
      return parts.map((part, index) => {
        if (!part) return null;
        const lowerPart = part.toLowerCase();
        if (lowerPart === '@w') return <span key={`${si}-${index}`} onClick={(e) => { e.stopPropagation(); setShowWIAModal(true); }} className="text-violet-400 font-semibold cursor-pointer hover:text-violet-300 transition">{part}</span>;
        if (W_TRIGGER_TAGS.includes(lowerPart)) return <span key={`${si}-${index}`} className="text-blue-400">{part}</span>;
        return <span key={`${si}-${index}`}>{part}</span>;
      });
    });
    
    return rendered;
  }, []);

  // Get filtered members for @ mention
  const getMentionSuggestions = useCallback(() => {
    const query = mentionQuery.toLowerCase();
    const members = selectableMembers.filter(m => 
      m.name.toLowerCase().includes(query) || 
      (m.cat && m.cat.toLowerCase().includes(query))
    ).slice(0, 5);
    
    // Add W option at the beginning if query matches
    const wOption = { id: 'w-ia', name: 'W', cat: 'W specialist', isWIA: true };
    if ('w'.includes(query) || 'sommelier'.includes(query)) {
      return [wOption, ...members];
    }
    return query ? members : [wOption, ...members.slice(0, 4)];
  }, [mentionQuery, selectableMembers]);

  // Handle mention selection
  const handleMentionSelect = useCallback((member) => {
    if (mentionStartIndex === null) return;
    
    // Replace @query with @Name
    const beforeMention = input.slice(0, mentionStartIndex);
    const afterMention = input.slice(mentionStartIndex + mentionQuery.length + 1); // +1 for @
    const newInput = `${beforeMention}@${member.name} ${afterMention}`;
    
    setInput(newInput);
    setShowMentionList(false);
    setMentionQuery('');
    setMentionStartIndex(null);
    
    // If @W is selected, trigger W IA immediately
    if (member.isWIA) {
      // Trigger W IA response immediately
      setTimeout(async () => {
        const intervention = await fetchWInterventionFromAI();
        setWiaIntervention({ suggestion: intervention, timestamp: Date.now() });
      }, 300);
    }
    
    // Focus back on input
    inputRef.current?.focus();
  }, [input, mentionStartIndex, mentionQuery]);

  // Handle input change with typing notification and @ mention detection
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInput(value);
    
    // Check for special @tags first (before mention list)
    const lowerValue = value.toLowerCase();
    
    // Directly open modals when @tags are detected
    if (lowerValue.endsWith('@wcash') || lowerValue.includes('@wcash ')) {
      setShowWcashModal(true);
      setShowMentionList(false);
      setTimeout(() => setInput(value.replace(/@wcash/gi, '').trim()), 50);
      return;
    }
    if (lowerValue.endsWith('@recado') || lowerValue.includes('@recado ')) {
      setShowRecadoModal(true);
      setShowMentionList(false);
      setTimeout(() => setInput(value.replace(/@recado/gi, '').trim()), 50);
      return;
    }
    if (lowerValue.endsWith('@conversa') || lowerValue.includes('@conversa ')) {
      setShowConversaModal(true);
      setShowMentionList(false);
      setTimeout(() => setInput(value.replace(/@conversa/gi, '').trim()), 50);
      return;
    }
    
    // Detect @ mention
    const textBeforeCursor = value.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      // Check if @ is at the start or after a space
      const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || atIndex === 0) {
        const query = textBeforeCursor.slice(atIndex + 1);
        // Only show if query doesn't contain space (still typing the mention)
        if (!query.includes(' ')) {
          setShowMentionList(true);
          setMentionQuery(query);
          setMentionStartIndex(atIndex);
        } else {
          setShowMentionList(false);
        }
      } else {
        setShowMentionList(false);
      }
    } else {
      setShowMentionList(false);
    }
    
    // Send typing status (debounced)
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (value.trim()) {
      sendTypingStatus();
      typingTimeoutRef.current = setTimeout(() => {
        // Stop sending after 3 seconds of no typing
      }, 3000);
    }
  }, [sendTypingStatus]);

  // Poll for incoming calls (every 1.5s for better responsiveness)
  useEffect(() => {
    if (!currentMember) return;
    
    // Check immediately on mount
    const checkIncoming = async () => {
      try {
        const { data } = await axios.get(`${API}/call/incoming/${currentMember.id}`);
        // Only show incoming call if it has an offer (caller has finished WebRTC setup)
        if (data?.id && data?.offer && !activeCall) {
          console.log('[Chat] Incoming call detected:', data.id);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
          setActiveCall({ id: data.id, callerName: data.caller_name, calleeName: currentMember.name, isCaller: false });
        }
      } catch (e) {
        console.log('[Chat] Error checking incoming calls:', e.message);
      }
    };
    
    checkIncoming(); // Check immediately
    incomingPollRef.current = setInterval(checkIncoming, 1000);
    return () => { if (incomingPollRef.current) clearInterval(incomingPollRef.current); };
  }, [currentMember, activeCall]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !currentMember || loading) return;
    setInput('');
    isAtBottomRef.current = true;
    
    try {
      setLoading(true);
      
      // Check if replying to W IA - handle differently
      if (replyingTo?.isWIA) {
        setReplyingTo(null);
        await sendMessageToWIA(text);
        return;
      }
      
      // If encryption is enabled, encrypt the message
      let messageToSend = text;
      let messageType = 'text';
      
      if (encryptionEnabled) {
        const pin = getMemberPin(currentMember);
        messageToSend = encryptMessage(text, pin);
        messageType = 'encrypted'; // Mark as encrypted
      }
      
      // Include reply reference if replying
      const replyData = replyingTo ? {
        reply_to_id: replyingTo.id,
        reply_to_name: replyingTo.sender_name,
        reply_to_preview: replyingTo.message_type === 'voice' ? '🎤 Voz' : 
                          replyingTo.message_type === 'image' ? '📷 Imagem' :
                          replyingTo.message?.slice(0, 30)
      } : {};
      
      // Optimistic update - show message immediately
      const optimisticMsg = {
        id: `temp_${Date.now()}`,
        sender_id: currentMember.id,
        sender_name: currentMember.name,
        message: text,
        message_type: messageType === 'encrypted' ? 'encrypted' : 'text',
        timestamp: new Date().toISOString(),
        recipient_id: selectedRecipient?.id || null,
        self_destruct_seconds: selfDestructSeconds || null,
        ...(replyData.reply_to_id ? replyData : {}),
      };
      setMessages(prev => [...prev, optimisticMsg]);
      isAtBottomRef.current = true;

      await sendChatMessage({ 
        sender_id: currentMember.id, 
        sender_name: currentMember.name, 
        room: 'general', 
        recipient_id: selectedRecipient?.id || null, 
        message: messageToSend, 
        message_type: messageType,
        self_destruct_seconds: selfDestructSeconds || null,
        ...replyData
      });
      
      setReplyingTo(null);
      setSelfDestructSeconds(null);
      loadMessages();
    } catch (e) { showNotif('Erro ao enviar', 'warn'); } finally { setLoading(false); }
  };

  const playAudio = (msgId, fileData, fileType) => {
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; if (playingAudioId === msgId) { setPlayingAudioId(null); return; } }
    // Strip data URI prefix if present (e.g. "data:audio/mp3;base64,...")
    let rawB64 = fileData;
    let detectedType = fileType || 'audio/mp4';
    if (typeof fileData === 'string' && fileData.startsWith('data:')) {
      const match = fileData.match(/^data:(audio\/[^;]+);base64,(.+)$/);
      if (match) {
        detectedType = match[1];
        rawB64 = match[2];
      }
    }
    const blob = b64toBlob(rawB64, detectedType);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudioRef.current = audio;
    setPlayingAudioId(msgId);
    audio.onended = () => { URL.revokeObjectURL(url); currentAudioRef.current = null; setPlayingAudioId(null); };
    audio.onerror = () => { URL.revokeObjectURL(url); currentAudioRef.current = null; setPlayingAudioId(null); };
    audio.play().catch(() => setPlayingAudioId(null));
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getRecordingMime();
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];
      const actualMime = mediaRecorderRef.current.mimeType || mimeType || 'audio/webm';
      mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMime });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          try {
            setLoading(true);
            await sendChatMessage({ sender_id: currentMember.id, sender_name: currentMember.name, room: 'general', recipient_id: selectedRecipient?.id || null, message: 'Mensagem de voz', message_type: 'voice', file_data: base64, file_name: `voice.${actualMime.includes('mp4') ? 'm4a' : 'webm'}`, file_type: actualMime });
            isAtBottomRef.current = true;
            loadMessages();
          } catch (err) { showNotif('Erro ao enviar audio', 'warn'); } finally { setLoading(false); }
        };
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecordingVoice(true);
    } catch (e) { showNotif('Microfone indisponivel', 'warn'); }
  };

  const stopVoiceRecording = () => { if (mediaRecorderRef.current && isRecordingVoice) { mediaRecorderRef.current.stop(); setIsRecordingVoice(false); } };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentMember) return;
    if (file.size > 5 * 1024 * 1024) { showNotif('Max 5MB', 'warn'); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1];
      const isImage = file.type.startsWith('image/');
      
      // Check if encryption is enabled
      let fileDataToSend = base64;
      let messageType = isImage ? 'image' : 'file';
      
      if (encryptionEnabled) {
        const pin = getMemberPin(currentMember);
        // Encrypt the file data
        fileDataToSend = encryptMessage(base64, pin);
        messageType = 'encrypted_' + messageType; // encrypted_image or encrypted_file
      }
      
      // Include reply reference if replying
      const replyData = replyingTo ? {
        reply_to_id: replyingTo.id,
        reply_to_name: replyingTo.sender_name,
        reply_to_preview: replyingTo.message_type === 'voice' ? '🎤 Voz' : 
                          replyingTo.message_type === 'image' ? '📷 Imagem' :
                          replyingTo.message?.slice(0, 30)
      } : {};
      
      try {
        setLoading(true);
        await sendChatMessage({ 
          sender_id: currentMember.id, 
          sender_name: currentMember.name, 
          room: 'general', 
          recipient_id: selectedRecipient?.id || null, 
          message: isImage ? (encryptionEnabled ? '🔒 Foto criptografada' : 'Foto') : (encryptionEnabled ? '🔒 Arquivo criptografado' : file.name), 
          message_type: messageType, 
          file_data: fileDataToSend, 
          file_name: file.name, 
          file_type: file.type,
          ...replyData
        });
        setReplyingTo(null);
        isAtBottomRef.current = true;
        loadMessages();
      } catch (err) { showNotif('Erro', 'warn'); } finally { setLoading(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const initiateCall = async () => {
    if (!selectedRecipient) { showNotif('Selecione um contato', 'warn'); return; }
    // Request mic permission before starting the call
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop()); // release immediately, just testing permission
    } catch (e) {
      showNotif('Permita o acesso ao microfone para ligar', 'warn');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/call/initiate`, { caller_id: currentMember.id, caller_name: currentMember.name, callee_id: selectedRecipient.id });
      setActiveCall({ id: data.id, callerName: currentMember.name, calleeName: selectedRecipient.name, isCaller: true });
    } catch (e) { showNotif('Erro na chamada', 'warn'); }
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const getInitials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Context menu handlers (long press)
  const handleLongPressStart = (msg, e) => {
    // Prevent default text selection on mobile
    e.preventDefault();
    
    const timer = setTimeout(() => {
      // Get touch/mouse position
      let x, y;
      if (e.touches && e.touches[0]) {
        x = e.touches[0].clientX;
        y = e.touches[0].clientY;
      } else {
        x = e.clientX;
        y = e.clientY;
      }
      
      // Position menu near the touch point but ensure it stays on screen
      const menuWidth = 160;
      const menuHeight = 150;
      const safeX = Math.min(Math.max(x - 80, 10), window.innerWidth - menuWidth - 10);
      const safeY = Math.min(Math.max(y - menuHeight - 20, 10), window.innerHeight - menuHeight - 10);
      
      setContextMenu({ 
        msg, 
        x: safeX, 
        y: safeY 
      });
      if (navigator.vibrate) navigator.vibrate(30);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Phone button handler - click to show options (like WBusiness)
  const handlePhoneClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedRecipient) {
      showNotif('Selecione um contato', 'warn');
      return;
    }
    
    // Calculate position for tooltip near button
    if (phoneButtonRef.current) {
      const rect = phoneButtonRef.current.getBoundingClientRect();
      setPhoneTooltipPosition({
        top: rect.top - 10,
        left: rect.left + rect.width / 2,
      });
    }
    
    setShowPhoneOptions(!showPhoneOptions);
  };

  // Close phone options when clicking outside
  useEffect(() => {
    if (showPhoneOptions) {
      const handleClickOutside = () => setShowPhoneOptions(false);
      setTimeout(() => document.addEventListener('click', handleClickOutside), 100);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showPhoneOptions]);

  const handleCopyMessage = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.message || '');
      showNotif('Copiado!', 'success');
    } catch (e) {
      showNotif('Erro ao copiar', 'warn');
    }
    setContextMenu(null);
  };

  const handleShareMessage = async (msg) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'WClub Chat',
          text: `${msg.sender_name}: ${msg.message}`
        });
      } catch (e) {}
    } else {
      handleCopyMessage(msg);
    }
    setContextMenu(null);
  };

  const handleDeleteMessage = async (msg) => {
    try {
      await axios.delete(`${API}/chat/messages/${msg.id}`);
      loadMessages();
      showNotif('Deletado', 'success');
    } catch (e) {
      showNotif('Erro ao deletar', 'warn');
    }
    setContextMenu(null);
  };

  // Handle reaction toggle
  const handleReaction = async (msg, reactionType) => {
    if (!currentMember) return;
    try {
      await axios.post(`${API}/chat/messages/${msg.id}/reactions`, {
        member_id: currentMember.id,
        reaction_type: reactionType
      });
      loadMessages();
    } catch (e) {
      showNotif('Erro ao reagir', 'warn');
    }
    setContextMenu(null);
  };

  // Check if current member has reacted with specific type
  const hasReacted = (msg, reactionType) => {
    if (!msg.reactions || !currentMember) return false;
    return msg.reactions[reactionType]?.includes(currentMember.id);
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const AudioWaveform = ({ isOwn, isPlaying }) => (
    <div className="flex items-center gap-[1px] flex-1 h-5">
      {[...Array(24)].map((_, i) => {
        const h = 2 + Math.sin(i * 0.7) * 6 + Math.cos(i * 1.4) * 3;
        return (
          <motion.span key={i}
            className={`w-[1.5px] rounded-full ${isOwn ? 'bg-white/50' : 'bg-white/25'}`}
            animate={isPlaying ? { height: [h, h * 0.3, h, h * 0.6, h] } : { height: h }}
            transition={isPlaying ? { duration: 0.7, repeat: Infinity, delay: i * 0.025 } : {}}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );

  // Scrambling text component - static container with animated content
  const ScrambleText = React.memo(({ length = 30 }) => {
    const [displayText, setDisplayText] = useState('');
    const charSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const fixedWidth = length * 7.5; // Fixed pixel width based on approximate monospace char width
    
    useEffect(() => {
      // Generate initial text
      const generate = () => {
        let result = '';
        for (let i = 0; i < length; i++) {
          result += charSet[Math.floor(Math.random() * charSet.length)];
        }
        return result;
      };
      
      setDisplayText(generate());
      
      // Animate slowly
      const interval = setInterval(() => {
        setDisplayText(generate());
      }, 400);
      
      return () => clearInterval(interval);
    }, [length]);
    
    return (
      <div 
        style={{ 
          width: `${fixedWidth}px`,
          height: '18px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <span 
          style={{ 
            fontFamily: 'monospace',
            fontSize: '12px',
            letterSpacing: '0.5px',
            color: 'rgba(167, 139, 250, 0.6)',
            position: 'absolute',
            top: 0,
            left: 0,
            whiteSpace: 'pre',
          }}
        >
          {displayText}
        </span>
      </div>
    );
  });

  const renderMessage = (msg, index, totalMessages) => {
    const isOwn = msg.sender_id === currentMember.id;
    const isWIA = msg.sender_id === 'w-ia'; // W IA messages
    const isPlaying = playingAudioId === msg.id;
    const isEncrypted = msg.message_type === 'encrypted';
    const isDecrypted = decryptedMessages[msg.id];
    const isSwiping = swipingMsgId === msg.id;
    const currentSwipeX = isSwiping ? swipeX : 0;
    
    // Self-destruct message check
    const isSelfDestruct = msg.self_destruct_seconds && msg.expires_at;
    const isExpired = msg.is_expired;
    
    // If message is expired, show special expired view
    if (isExpired) {
      return (
        <motion.div 
          key={msg.id} 
          initial={{ opacity: 1 }}
          animate={{ opacity: 0.6 }}
          className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-end gap-2 relative`}
          data-testid={`chat-msg-${msg.id}`}
        >
          <div className={`relative px-4 py-2 rounded-[14px] ${isOwn ? 'rounded-br-[4px]' : 'rounded-bl-[4px]'}`}
            style={{
              background: 'linear-gradient(135deg, rgba(100,100,100,0.1) 0%, rgba(80,80,80,0.05) 100%)',
              border: '1px dashed rgba(255,255,255,0.15)',
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[12px] text-white/30 font-['Jost'] italic">Esta mensagem expirou</span>
            </div>
          </div>
        </motion.div>
      );
    }

    // Swipe handlers
    const handleSwipeStart = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      swipeStartX.current = clientX;
      setSwipingMsgId(msg.id);
    };

    const handleSwipeMove = (e) => {
      if (swipingMsgId !== msg.id) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const diff = clientX - swipeStartX.current;
      // Only allow swipe right (positive diff), max 80px
      if (diff > 0) {
        setSwipeX(Math.min(diff, 80));
      }
    };

    const handleSwipeEnd = () => {
      if (swipeX > 50) {
        // Trigger reply
        setReplyingTo(msg);
      }
      setSwipeX(0);
      setSwipingMsgId(null);
    };

    return (
      <motion.div 
        key={msg.id} 
        initial={{ opacity: 0, y: 4 }} 
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-end gap-2 relative ${highlightedMsgId === msg.id ? 'z-10' : ''}`}
        data-testid={`chat-msg-${msg.id}`}
        data-message-id={msg.id}
      >
        
        {/* Highlight overlay when this message is referenced */}
        {highlightedMsgId === msg.id && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2, times: [0, 0.1, 0.7, 1] }}
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ 
              background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
            }}
          />
        )}
        
        {/* Reply indicator - shows when swiping */}
        {currentSwipeX > 20 && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: currentSwipeX / 80, scale: 0.8 + (currentSwipeX / 400) }}
            className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center z-0"
          >
            <svg className="w-5 h-5 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </motion.div>
        )}
        
        {!isOwn && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'transparent' }}>
            <span className="text-[9px] font-['Jost'] text-white/50 font-semibold">{getInitials(msg.sender_name)}</span>
          </div>
        )}
        
        {/* Balão da mensagem - with swipe */}
        <motion.div 
          animate={{ x: currentSwipeX }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className={`relative max-w-[78%] ${isOwn ? 'rounded-[20px] rounded-br-[6px]' : 'rounded-[20px] rounded-bl-[6px]'}`}
          onTouchStart={(e) => { handleSwipeStart(e); handleLongPressStart(msg, e); }}
          onTouchMove={handleSwipeMove}
          onTouchEnd={() => { handleSwipeEnd(); handleLongPressEnd(); }}
          onTouchCancel={() => { handleSwipeEnd(); handleLongPressEnd(); }}
          onMouseDown={(e) => handleLongPressStart(msg, e)}
          onMouseUp={handleLongPressEnd}
          onMouseLeave={handleLongPressEnd}
          onContextMenu={(e) => { e.preventDefault(); handleLongPressEnd(); setContextMenu({ msg, x: Math.min(e.clientX, window.innerWidth - 170), y: Math.min(e.clientY - 50, window.innerHeight - 200) }); }}
          style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
        >
          {/* Message Bubble */}
          <div className={`relative ${msg.message_type === 'location' ? 'px-2 py-1.5 z-10' : `px-3.5 py-2 z-10 ${
            isOwn
              ? 'imsg-bubble-own imsg-bubble-own-tail'
              : 'imsg-bubble-recv imsg-bubble-recv-tail'
          } ${isWIA ? 'cursor-pointer' : ''} ${isSelfDestruct ? 'self-destruct-pulse' : ''}`}`}
          onClick={isWIA ? (e) => { e.stopPropagation(); activateWiaConversation(msg.id); } : undefined}
          style={msg.message_type === 'location' ? { background: 'transparent', border: 'none' } : {
            background: isWIA
              ? 'rgba(10,132,255,0.04)'
              : isEncrypted && !isDecrypted
                ? 'linear-gradient(135deg, rgba(139,92,246,0.14) 0%, rgba(139,92,246,0.06) 100%)'
                : isSelfDestruct
                  ? 'linear-gradient(135deg, rgba(249,115,22,0.14) 0%, rgba(234,88,12,0.08) 100%)'
                  : undefined,
            border: isWIA
              ? (wiaConversationActive || wiaActivatedMsgId === msg.id)
                ? '1.5px solid rgba(10,132,255,0.95)'
                : '1px solid rgba(10,132,255,0.6)'
              : isEncrypted && !isDecrypted 
                ? '1px solid rgba(139,92,246,0.35)'
                : isSelfDestruct
                  ? '1.5px solid rgba(249,115,22,0.5)'
                  : undefined,
            boxShadow: isWIA
              ? (wiaConversationActive || wiaActivatedMsgId === msg.id)
                ? '0 0 24px rgba(10,132,255,0.55), 0 0 48px rgba(10,132,255,0.25), inset 0 1px 0 rgba(10,132,255,0.20)'
                : '0 0 14px rgba(10,132,255,0.25), 0 0 28px rgba(10,132,255,0.12)'
              : isSelfDestruct
                ? '0 0 15px rgba(249,115,22,0.25), inset 0 1px 0 rgba(249,115,22,0.15)'
                : undefined,
            backdropFilter: isWIA ? 'blur(20px) saturate(1.4)' : undefined,
            WebkitBackdropFilter: isWIA ? 'blur(20px) saturate(1.4)' : undefined,
            transition: 'all 0.3s ease',
          }}>
            {/* W IA Verified Badge - Click to activate conversation, glows when active */}
            {isWIA && (
              <motion.div 
                animate={{
                  scale: (wiaConversationActive || wiaActivatedMsgId === msg.id) ? [1, 1.2, 1] : 1,
                  boxShadow: (wiaConversationActive || wiaActivatedMsgId === msg.id) 
                    ? '0 0 20px rgba(10,132,255,1), 0 0 40px rgba(10,132,255,0.6)' 
                    : '0 0 10px rgba(10,132,255,0.6)'
                }}
                transition={{ duration: 0.5 }}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center shadow-lg z-20"
                style={{ background: '#0a84ff' }}
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
            )}
            {/* Reply reference - if message is replying to another */}
            {msg.reply_to_id && (
              <div 
                className="mb-2 px-2 py-1.5 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-violet-500/20 transition active:scale-[0.98]"
                style={{ 
                  background: 'rgba(139, 92, 246, 0.1)', 
                  borderLeft: '2px solid rgba(139, 92, 246, 0.6)' 
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollToMessage(msg.reply_to_id);
                }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] text-violet-400 font-['Jost'] font-medium">{msg.reply_to_name}</p>
                  <p className="text-[10px] text-white/40 font-['Jost'] truncate">{msg.reply_to_preview}</p>
                </div>
                <svg className="w-3 h-3 text-violet-400/50 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </div>
            )}
            
            {/* Sender name - show for all received messages */}
            {!isOwn && (
              <div className={`flex items-center gap-1.5 mb-1`}>
                <p className={`text-[10px] font-['Jost'] font-medium ${isWIA ? 'text-blue-400' : 'text-white/50'}`}>
                  {isWIA ? 'W' : msg.sender_name?.split(' ')[0]}
                </p>
                {isWIA && (
                  <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
            )}
            
            {/* Encrypted message - show lock and animated scrambled text */}
            {isEncrypted && !isDecrypted && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-3.5 h-3.5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[10px] text-violet-400 font-['Jost']">Mensagem criptografada</span>
                </div>
                <div className="text-[13px] leading-[1.6] text-violet-300/50 select-none overflow-hidden">
                  <ScrambleText length={Math.min(msg.message?.length || 25, 40)} seed={msg.id} />
                </div>
                <p className="text-[9px] text-violet-300/40 mt-2 font-['Jost']">Segure para opções</p>
              </div>
            )}
            
            {/* Encrypted message - decrypted view */}
            {isEncrypted && isDecrypted && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[10px] text-green-400/70 font-['Jost']">Desbloqueada</span>
                </div>
                <p className="text-[14px] leading-[1.5] text-white font-['Jost']">{isDecrypted}</p>
              </div>
            )}
            
            {/* Location Pin Message */}
            {msg.message_type === 'location' && (
              <LocationPinMessage 
                message={msg.message} 
                isOwn={isOwn} 
                formatTime={formatTime} 
                timestamp={msg.timestamp} 
              />
            )}
            
            {/* Normal text message - with highlighted tags and translation */}
            {msg.message_type === 'text' && (
              <div>
                {/* Show translation if available and different from original */}
                {translationEnabled && translations[msg.id] && !translations[msg.id].sameLanguage && !translations[msg.id].failed && translations[msg.id].text !== msg.message ? (
                  <div>
                    {/* For own messages: show original first, then translation */}
                    {isOwn ? (
                      <>
                        {/* Original text (main) */}
                        <p className="text-[14px] leading-[1.5] text-white font-['Jost']">
                          {renderMessageText(msg.message, msg.message_type)}
                        </p>
                        {/* Translation below with globe icon */}
                        <div className="mt-1.5 pt-1.5 border-t border-white/5">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <svg className="w-2.5 h-2.5 text-green-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-[9px] text-green-400/70 font-medium uppercase tracking-wider font-['Jost']">
                              → {translations[msg.id].targetLang?.toUpperCase() || userLanguage?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-[11px] leading-[1.4] text-white/50 font-['Jost'] italic">
                            {renderMessageText(translations[msg.id].text)}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* For others' messages: show translation first, then original */}
                        <p className="text-[14px] leading-[1.5] text-white font-['Jost']">
                          {renderMessageText(translations[msg.id].text)}
                        </p>
                        {/* Original text (smaller, faded) with language badge */}
                        <div className="mt-1.5 pt-1.5 border-t border-white/5">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <svg className="w-2.5 h-2.5 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-[9px] text-blue-400/70 font-medium uppercase tracking-wider font-['Jost']">
                              {translations[msg.id].sourceLang?.toUpperCase() || 'AUTO'}
                            </span>
                          </div>
                          <p className="text-[11px] leading-[1.4] text-white/40 font-['Jost'] italic">
                            {renderMessageText(msg.message, msg.message_type)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-[14px] leading-[1.5] text-white font-['Jost']">
                    {renderMessageText(msg.message, msg.message_type)}
                  </p>
                )}
              </div>
            )}
            
            {/* Encrypted image - locked */}
            {msg.message_type === 'encrypted_image' && !decryptedMessages[msg.id] && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-3.5 h-3.5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[10px] text-violet-400 font-['Jost']">Imagem criptografada</span>
                </div>
                <div className="w-32 h-24 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(139,92,246,0.05) 100%)' }}>
                  <svg className="w-8 h-8 text-violet-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-[9px] text-violet-300/40 mt-2 font-['Jost']">Segure para desbloquear</p>
              </div>
            )}
            
            {/* Encrypted image - unlocked */}
            {msg.message_type === 'encrypted_image' && decryptedMessages[msg.id] && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[10px] text-green-400/70 font-['Jost']">Desbloqueada</span>
                </div>
                <img src={`data:${msg.file_type};base64,${decryptedMessages[msg.id]}`} alt="" className="max-w-full rounded-xl" />
              </div>
            )}
            
            {/* Encrypted file - locked */}
            {msg.message_type === 'encrypted_file' && !decryptedMessages[msg.id] && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-3.5 h-3.5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[10px] text-violet-400 font-['Jost']">Arquivo criptografado</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(139,92,246,0.15)' }}>
                  <svg className="w-5 h-5 text-violet-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-[11px] text-violet-300/60 font-['Jost']">{msg.file_name}</span>
                </div>
                <p className="text-[9px] text-violet-300/40 mt-2 font-['Jost']">Segure para desbloquear</p>
              </div>
            )}
            
            {/* Encrypted file - unlocked */}
            {msg.message_type === 'encrypted_file' && decryptedMessages[msg.id] && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[10px] text-green-400/70 font-['Jost']">Desbloqueado</span>
                </div>
                <a href={`data:${msg.file_type};base64,${decryptedMessages[msg.id]}`} download={msg.file_name} className="flex items-center gap-2 text-[12px] text-white/80 underline decoration-white/30">
                  <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  {msg.file_name}
                </a>
              </div>
            )}
            
            {msg.message_type === 'voice' && (
              <div>
                <div className="flex items-center gap-2 min-w-[140px] cursor-pointer" onClick={(e) => { e.stopPropagation(); playAudio(msg.id, msg.file_data, msg.file_type); }} data-testid={`voice-msg-${msg.id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition ${isPlaying ? 'bg-white/20' : 'bg-white/10'}`}>
                    {isPlaying
                      ? <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20"><rect x="5" y="4" width="3.5" height="12" rx="1" /><rect x="11.5" y="4" width="3.5" height="12" rx="1" /></svg>
                      : <svg className="w-3.5 h-3.5 text-white/70 ml-0.5" fill="currentColor" viewBox="0 0 20 20"><path d="M6.3 4.2a1 1 0 011.4-.2l7 5a1 1 0 010 1.6l-7 5A1 1 0 016 15V5a1 1 0 01.3-.8z" /></svg>
                    }
                  </div>
                  <AudioWaveform isOwn={isOwn} isPlaying={isPlaying} />
                </div>
                {/* Transcription section */}
                {transcriptions[msg.id] ? (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <svg className="w-2.5 h-2.5 text-purple-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <span className="text-[9px] text-purple-400/70 font-medium uppercase tracking-wider font-['Jost']">Transcrição</span>
                    </div>
                    <p className="text-[12px] leading-[1.4] text-white/70 font-['Jost'] italic">
                      "{transcriptions[msg.id]}"
                    </p>
                  </div>
                ) : msg.transcription ? (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <svg className="w-2.5 h-2.5 text-purple-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <span className="text-[9px] text-purple-400/70 font-medium uppercase tracking-wider font-['Jost']">Transcrição</span>
                    </div>
                    <p className="text-[12px] leading-[1.4] text-white/70 font-['Jost'] italic">
                      "{msg.transcription}"
                    </p>
                  </div>
                ) : (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => { e.stopPropagation(); transcribeVoiceMessage(msg.id); }}
                    disabled={transcribingIds.has(msg.id)}
                    className="mt-2 flex items-center gap-1.5 text-[10px] text-purple-400/70 hover:text-purple-400 transition font-['Jost']"
                  >
                    {transcribingIds.has(msg.id) ? (
                      <>
                        <div className="w-2.5 h-2.5 border border-purple-400/50 border-t-transparent rounded-full animate-spin" />
                        <span>Transcrevendo...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        <span>Transcrever</span>
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            )}
            {msg.message_type === 'image' && <img src={`data:${msg.file_type};base64,${msg.file_data}`} alt="" className="max-w-full rounded-xl" loading="lazy" />}
            {msg.message_type === 'file' && (
              <a href={`data:${msg.file_type};base64,${msg.file_data}`} download={msg.file_name} className="flex items-center gap-2 text-[12px] text-white/80 underline decoration-white/30">
                <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                {msg.file_name}
              </a>
            )}
            {msg.message_type !== 'location' && (
            <div className={`flex items-center gap-1.5 mt-2 ${isOwn ? 'justify-end' : ''}`}>
              <p className="text-[10px] font-['Jost'] text-white/50">{formatTime(msg.timestamp)}</p>
              {/* Self-destruct countdown */}
              {isSelfDestruct && msg.expires_at && (
                <SelfDestructCountdown expiresAt={msg.expires_at} messageId={msg.id} />
              )}
              {isOwn && (
                <svg className="w-3 h-3 text-blue-400/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            )}
            
            {/* Reactions display */}
            {msg.reactions && Object.keys(msg.reactions).length > 0 && (
              <div className={`flex flex-wrap gap-2 mt-2 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {Object.entries(msg.reactions).map(([reactionType, memberIds]) => {
                  if (!memberIds || memberIds.length === 0) return null;
                  const config = REACTION_CONFIG[reactionType];
                  const Icon = ReactionIcons[reactionType];
                  const isMine = memberIds.includes(currentMember?.id);
                  return (
                    <motion.button
                      key={reactionType}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => { e.stopPropagation(); handleReaction(msg, reactionType); }}
                      className="flex items-center gap-1 transition-all"
                    >
                      {Icon && <Icon size={14} filled={false} color={config?.color || '#fff'} />}
                      <span className="text-[11px] font-['Jost']" style={{ color: config?.color || '#fff', opacity: isMine ? 1 : 0.6 }}>{memberIds.length}</span>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  if (!currentMember) {
    return <div className="flex items-center justify-center h-[60vh]" data-testid="chat-no-member"><p className="text-white/15 text-xs font-['Jost'] tracking-[0.2em]">Selecione um membro</p></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-black overflow-x-hidden wchat-imsg" style={{ overscrollBehaviorX: 'none', touchAction: 'pan-y' }} data-testid="chat-section">
      {showIntro && (
        <WchatIntro
          currentMember={currentMember}
          members={allMembers}
          onDone={() => {
            try { sessionStorage.setItem('wchat_intro_seen', '1'); } catch { /* ignore */ }
            setShowIntro(false);
          }}
        />
      )}
      <style>{`
        /* iMessage theme variables */
        .wchat-imsg {
          --imsg-blue: #0a84ff;
          --imsg-blue-2: #007aff;
          --imsg-bg-recv: #2c2c2e;
          --imsg-bg-recv-hi: #3a3a3c;
          --imsg-text: #ffffff;
          --imsg-text-recv: #f2f2f7;
          --imsg-muted: #8e8e93;
          --imsg-canvas: #000000;
          background: var(--imsg-canvas);
        }
        .wchat-imsg .imsg-bubble-own {
          background: linear-gradient(180deg, #0a84ff 0%, #0066f0 100%) !important;
          color: #ffffff !important;
          border: 1px solid rgba(10,132,255,0.4) !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15) !important;
        }
        .wchat-imsg .imsg-bubble-recv {
          background: rgba(255,255,255,0.06) !important;
          color: #f2f2f7 !important;
          border: 1px solid rgba(255,255,255,0.10) !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08) !important;
          backdrop-filter: blur(24px) saturate(1.6) !important;
          -webkit-backdrop-filter: blur(24px) saturate(1.6) !important;
        }
        .wchat-imsg .imsg-bubble-own, .wchat-imsg .imsg-bubble-recv {
          border-radius: 20px !important;
        }
        .wchat-imsg .imsg-bubble-own-tail {
          border-bottom-right-radius: 6px !important;
        }
        .wchat-imsg .imsg-bubble-recv-tail {
          border-bottom-left-radius: 6px !important;
        }
        /* Composer pill */
        .wchat-imsg .imsg-composer {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 22px;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }
        /* Send button */
        .wchat-imsg .imsg-send {
          background: linear-gradient(180deg, #0a84ff 0%, #0066f0 100%) !important;
          box-shadow: 0 2px 10px rgba(10,132,255,0.45), inset 0 1px 0 rgba(255,255,255,0.25) !important;
        }
        /* Header styling */
        .wchat-imsg .imsg-header {
          text-align: center;
        }
        /* Typing dots bubble (iMessage style) */
        .wchat-imsg .imsg-typing {
          background: #2c2c2e !important;
          border: 1px solid rgba(255,255,255,0.06) !important;
        }

        @keyframes chatBorderLight {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -1600; }
        }
        .chat-border-light {
          animation: chatBorderLight 8s linear infinite;
        }
        
        /* Self-destruct message pulse animation */
        @keyframes selfDestructPulse {
          0%, 100% { 
            border-color: rgba(249,115,22,0.5);
            box-shadow: 0 0 15px rgba(249,115,22,0.25);
          }
          50% { 
            border-color: rgba(249,115,22,0.8);
            box-shadow: 0 0 25px rgba(249,115,22,0.4);
          }
        }
        .self-destruct-pulse {
          animation: selfDestructPulse 2s ease-in-out infinite;
        }
        
        /* Screenshot protection - makes screenshots partially black */
        .screenshot-protect {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }
        
        /* DRM-style protection (experimental - works on some browsers) */
        @media screen {
          .chat-messages-protected {
            /* This attempts to prevent screenshots on supporting browsers */
          }
        }
      `}</style>
      
      {/* Main content area - takes remaining space */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}>
      <div className="flex flex-col flex-1 overflow-hidden" data-testid="chat-container">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`mx-3 mt-2 px-4 py-2 rounded-2xl text-xs font-['Jost'] flex items-center justify-between ${
              notification.type === 'success' ? 'bg-green-500/10 text-green-400/70' :
              notification.type === 'warn' ? 'bg-amber-500/8 text-amber-400/70' :
              'bg-white/5 text-white/40'
            }`} data-testid="chat-notification">
            <span>{notification.msg}</span>
            <button onClick={() => setNotification(null)} className="ml-2 text-white/20 touch-manipulation" data-testid="notif-close">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── HEADER - iMessage style: centered avatar + name ─── */}
      <div className="py-2.5 flex-shrink-0 imsg-header relative" 
           style={{ background: 'transparent', paddingLeft: '44px', paddingRight: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
           data-testid="chat-header">
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-center gap-0.5 min-w-0 flex-1 cursor-pointer"
               onClick={() => { if (!selectedRecipient) { setShowRecipientSelector(true); setShowSearch(false); } }}>
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3a3a3c 0%, #1c1c1e 100%)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-[12px] font-['Jost'] text-white/90 font-medium">
                  {selectedRecipient ? getInitials(selectedRecipient.name) : 'W'}
                </span>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-black" />
            </div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-[13px] font-['Jost'] font-medium text-white tracking-tight truncate max-w-[160px]" data-testid="chat-title">
                {selectedRecipient ? selectedRecipient.name : 'Wchat'}
              </h2>
              {unreadCount > 0 && (
                <span className="px-1 py-0.5 bg-red-500 text-white text-[8px] font-['Jost'] font-semibold rounded-full min-w-[16px] text-center" data-testid="chat-unread-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 flex-shrink-0">
            {/* Search button */}
            <motion.button whileTap={{ scale: 0.9 }}
              onClick={() => { setShowSearch(true); setShowRecipientSelector(false); }}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all touch-manipulation active:bg-white/10"
              data-testid="chat-search-btn">
              <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="#ffffff" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </motion.button>
            {/* Call button */}
            <motion.button 
              ref={phoneButtonRef}
              whileTap={{ scale: 0.9 }} 
              onClick={handlePhoneClick}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all touch-manipulation active:bg-white/10"
              data-testid="call-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Phone options tooltip - Rendered via Portal (opens to left) */}
        {showPhoneOptions && selectedRecipient && createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed z-[9999]"
              style={{ 
                top: phoneTooltipPosition.top + 40,
                right: 16,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="relative rounded-2xl p-2 shadow-2xl min-w-[180px] backdrop-blur-xl"
                style={{ 
                  background: 'linear-gradient(145deg, rgba(20,25,40,0.98) 0%, rgba(10,15,25,0.99) 100%)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.6)'
                }}
              >
                {/* Arrow pointing up */}
                <div 
                  className="absolute top-[-6px] right-[20px] w-3 h-3 rotate-45"
                  style={{ background: 'rgba(20,25,40,0.98)', borderTop: '1px solid rgba(255,255,255,0.15)', borderLeft: '1px solid rgba(255,255,255,0.15)' }}
                />
                <a
                  href={`tel:${selectedRecipient.phone?.replace(/\D/g, '') || ''}`}
                  onClick={() => setShowPhoneOptions(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/10 rounded-xl transition-all"
                >
                  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Ligar</span>
                </a>
                <a
                  href={`https://wa.me/${selectedRecipient.phone?.replace(/\D/g, '') || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowPhoneOptions(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/10 rounded-xl transition-all"
                >
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
                <a
                  href={`facetime:${selectedRecipient.phone?.replace(/\D/g, '') || ''}`}
                  onClick={() => setShowPhoneOptions(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/10 rounded-xl transition-all"
                >
                  <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>FaceTime</span>
                </a>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      </div>

      {/* ─── MEMBER SELECTOR BOTTOM SHEET (Portal) ─── */}
      {(showSearch || showRecipientSelector) && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => { setShowSearch(false); setShowRecipientSelector(false); setSearchQuery(''); }}
            data-testid="member-selector-modal"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl overflow-hidden"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15,15,20,0.98) 0%, rgba(5,5,10,0.99) 100%)',
                maxHeight: '70vh'
              }}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>
              
              {/* Header */}
              <div className="px-5 pb-3 border-b border-white/[0.06]">
                <h3 className="text-lg font-bold font-['Jost'] text-white/90">
                  {showSearch ? 'Buscar Sócio' : 'Conversa Privada'}
                </h3>
                <p className="text-xs text-white/40 font-['Jost']">
                  Selecione um sócio para conversar
                </p>
              </div>
              
              {/* Search input */}
              <div className="px-5 py-3">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar sócio..."
                    className="w-full pl-10 pr-10 py-3 bg-white/[0.05] border border-white/[0.08] rounded-xl text-sm text-white/80 placeholder-white/30 focus:border-violet-500/30 focus:outline-none transition font-['Jost']"
                    data-testid="chat-search-input"
                    autoFocus
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Members List */}
              <div className="px-5 pb-8 space-y-1 max-h-[50vh] overflow-y-auto">
                {/* Group option */}
                <button 
                  onClick={() => { setSelectedRecipient(null); setShowSearch(false); setShowRecipientSelector(false); setSearchQuery(''); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-['Jost'] transition touch-manipulation ${!selectedRecipient ? 'bg-violet-500/15 text-white/90' : 'text-white/60 hover:bg-white/[0.04]'}`}
                  data-testid="search-select-group"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.15)' }}>
                    <span className="text-[10px] text-violet-400 font-['Jost'] font-semibold">ALL</span>
                  </div>
                  <div className="flex-1 text-left">
                    <span className="block font-medium">Grupo - Todos</span>
                    <span className="text-[10px] text-white/40">Conversa com todos os sócios</span>
                  </div>
                  {unreadCounts['general'] > 0 && !selectedRecipient && (
                    <span className="min-w-[20px] h-5 px-1.5 bg-violet-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold">{unreadCounts['general'] > 99 ? '99+' : unreadCounts['general']}</span>
                  )}
                  {!selectedRecipient && (
                    <svg className="w-5 h-5 text-violet-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* W - Avisos option */}
                <button 
                  onClick={() => { setSelectedRecipient({ id: 'w-ia', name: 'W', cat: 'Avisos' }); setShowSearch(false); setShowRecipientSelector(false); setSearchQuery(''); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-['Jost'] transition touch-manipulation ${selectedRecipient?.id === 'w-ia' ? 'bg-cyan-500/15 text-white/90' : 'text-white/60 hover:bg-white/[0.04]'}`}
                  data-testid="search-select-wia"
                >
                  <div className="relative w-10 h-10 rounded-full flex items-center justify-center" style={{ background: selectedRecipient?.id === 'w-ia' ? 'rgba(34,211,238,0.15)' : 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(34,211,238,0.12))', border: '1px solid rgba(34,211,238,0.2)' }}>
                    <span className={`text-[14px] font-['Jost'] font-bold ${selectedRecipient?.id === 'w-ia' ? 'text-cyan-400' : 'text-cyan-400/70'}`}>W</span>
                  </div>
                  <div className="flex-1 text-left">
                    <span className="block font-medium">W - Avisos</span>
                    <span className="text-[10px] text-cyan-400/50">Convites, lembretes e notificações</span>
                  </div>
                  {unreadCounts['w-ia'] > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 bg-cyan-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold">{unreadCounts['w-ia'] > 99 ? '99+' : unreadCounts['w-ia']}</span>
                  )}
                  {selectedRecipient?.id === 'w-ia' && (
                    <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                
                {/* Divider */}
                <div className="py-2">
                  <p className="text-[10px] text-white/30 font-['Jost'] uppercase tracking-wider px-2">Sócios ({selectableMembers.length})</p>
                </div>
                
                {/* Members */}
                {(searchQuery ? filteredMembers : selectableMembers).length === 0 ? (
                  <p className="text-white/30 text-xs text-center py-6 font-['Jost']">Nenhum sócio encontrado</p>
                ) : (
                  (searchQuery ? filteredMembers : selectableMembers).map((m) => {
                    const isSelected = selectedRecipient?.id === m.id;
                    return (
                      <button 
                        key={m.id} 
                        onClick={() => { 
                          setSelectedRecipient(m); 
                          setShowSearch(false); 
                          setShowRecipientSelector(false);
                          setSearchQuery(''); 
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-['Jost'] transition touch-manipulation ${isSelected ? 'bg-violet-500/15 text-white/90' : 'text-white/60 hover:bg-white/[0.04]'}`}
                        data-testid={`search-select-${m.id}`}
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isSelected ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.05)' }}>
                          <span className={`text-[10px] font-['Jost'] font-semibold ${isSelected ? 'text-violet-400' : 'text-white/50'}`}>{getInitials(m.name)}</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className="block font-medium">{m.name}</span>
                          {m.cat && <span className="text-[10px] text-white/40 capitalize">{m.cat}</span>}
                        </div>
                        {unreadCounts[m.id] > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold">{unreadCounts[m.id] > 99 ? '99+' : unreadCounts[m.id]}</span>
                        )}
                        {isSelected && (
                          <svg className="w-5 h-5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* ─── MESSAGES - Black solid, no border ─── */}
      <div className="flex-1 overflow-hidden relative flex flex-col"
           style={{ background: '#000000' }}>
        <div ref={messagesContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2.5 screenshot-protect chat-messages-protected" style={{ overscrollBehaviorX: 'none', background: '#000000' }} data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0a84ff 0%, #0066f0 100%)', boxShadow: '0 8px 24px rgba(10,132,255,0.3)' }}>
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              </div>
              <p className="text-white/70 text-[14px] font-['Jost'] font-medium">Wchat</p>
              <p className="text-white/30 text-[11px] font-['Jost']">Nenhuma mensagem ainda</p>
            </div>
          ) : messages.map((msg, index) => renderMessage(msg, index, messages.length))}
          <div ref={messagesEndRef} />
        </div>

        {/* ─── INPUT BAR - Slim, with animated border effect ─── */}
        <div className="flex-shrink-0 relative bg-transparent px-3 py-2" style={{ paddingBottom: 'max(10px, env(safe-area-inset-bottom, 10px))' }}>
          {/* Scroll to bottom button - Liquid Glass style, smaller, aligned with send button direction */}
          <AnimatePresence>
            {showScrollBtn && (
              <motion.button initial={{ opacity: 0, y: 10, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.8 }}
                onClick={() => { scrollToBottom(); setShowScrollBtn(false); isAtBottomRef.current = true; }}
                className="absolute -top-10 right-4 w-5 h-5 rounded-full flex items-center justify-center touch-manipulation z-10"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)'
                }}
                data-testid="chat-scroll-bottom">
                <svg className="w-2.5 h-2.5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </motion.button>
            )}
          </AnimatePresence>
          
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" onChange={handleFileSelect} data-testid="chat-file-input" />
          <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={handleFileSelect} />
          
          {/* CSS for smooth continuous animated border light effect */}
          <style>{`
            @keyframes chatBarBorderLight {
              0% { stroke-dashoffset: 0; }
              100% { stroke-dashoffset: -1600; }
            }
            .chat-bar-border-light {
              animation: chatBarBorderLight 12s linear infinite;
            }
          `}</style>

          {/* W IA Conversation Active Indicator */}
          {wiaConversationActive && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="sticky top-0 z-20 flex items-center justify-center py-2 mb-2"
            >
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm"
                style={{ background: 'rgba(10,132,255,0.18)', border: '1px solid rgba(10,132,255,0.35)' }}>
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-[10px] font-['Jost'] text-blue-300">W conectado</span>
              </div>
            </motion.div>
          )}

          {/* Typing indicator */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                className="mb-2 flex items-center gap-2"
                data-testid="typing-indicator"
              >
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
                     style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-5 h-5 rounded-full bg-white/[0.08] flex items-center justify-center">
                    <span className="text-[8px] text-white/40 font-['Jost']">{isTyping.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <span className="text-[11px] text-white/40 font-['Jost']">{isTyping.name?.split(' ')[0]}</span>
                  <div className="flex items-center gap-[3px]">
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                      className="w-1 h-1 rounded-full bg-blue-400"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                      className="w-1 h-1 rounded-full bg-blue-400"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                      className="w-1 h-1 rounded-full bg-blue-400"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reply preview */}
          <AnimatePresence>
            {replyingTo && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-2 px-3 py-2 rounded-xl flex items-center justify-between"
                style={{ 
                  background: replyingTo.isWIA ? 'rgba(139, 92, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)', 
                  border: replyingTo.isWIA ? '1px solid rgba(139, 92, 246, 0.25)' : '1px solid rgba(59, 130, 246, 0.2)' 
                }}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className={`w-1 h-8 rounded-full flex-shrink-0 ${replyingTo.isWIA ? 'bg-violet-500' : 'bg-blue-500'}`} />
                  <div className="min-w-0">
                    <p className={`text-xs font-['Jost'] font-medium ${replyingTo.isWIA ? 'text-violet-400' : 'text-blue-400'}`}>
                      {replyingTo.isWIA ? 'W • specialist' : replyingTo.sender_name}
                    </p>
                    <p className="text-white/50 text-xs font-['Jost'] truncate flex items-center gap-1">
                      {replyingTo.message_type === 'voice' ? (
                        <><svg className="w-3 h-3 text-white/50 inline-block flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="2" width="6" height="11" rx="3" /><path d="M19 10v1a7 7 0 01-14 0v-1" /><line x1="12" y1="19" x2="12" y2="22" /></svg><span>Mensagem de voz</span></>
                      ) : replyingTo.message_type === 'image' ? (
                        <><svg className="w-3 h-3 text-white/50 inline-block flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><span>Imagem</span></>
                      ) : replyingTo.message_type === 'encrypted' ? (
                        <><svg className="w-3 h-3 text-violet-400 inline-block flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg><span className="text-violet-300/60">Criptografada</span></>
                      ) : (
                        replyingTo.message?.slice(0, 50) + (replyingTo.message?.length > 50 ? '...' : '')
                      )}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setReplyingTo(null)}
                  className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                >
                  <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Composer - iMessage style pill */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative imsg-composer overflow-hidden"
          >
            {/* Animated LED light traveling around border - same as WBusiness cards */}

            {/* Admin W Avisos Compose Panel */}
            {selectedRecipient?.id === 'w-ia' && isAdminLoggedIn() && (
              <AdminWAvisoPanel allMembers={allMembers} currentMember={currentMember} API={API} onSent={() => { loadMessages(); }} />
            )}

            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 90" preserveAspectRatio="none">
              <defs>
                <linearGradient id="chatBarLedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                  <stop offset="40%" stopColor="rgba(148,163,184,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.95)" />
                  <stop offset="60%" stopColor="rgba(148,163,184,0.6)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <rect x="1" y="1" width="398" height="88" rx="16" ry="16" fill="none" stroke="url(#chatBarLedGrad)" strokeWidth="1.5" strokeDasharray="1200" className="chat-bar-led" />
            </svg>
            <style>{`
              @keyframes chatBarLed {
                0% { stroke-dashoffset: 0; }
                100% { stroke-dashoffset: -2400; }
              }
              .chat-bar-led {
                animation: chatBarLed 10s linear infinite;
              }
            `}</style>
            
            <form onSubmit={handleSubmit} data-testid="chat-form">
              {/* @ Mention dropdown */}
              <AnimatePresence>
                {showMentionList && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute bottom-full left-2 right-2 mb-2 bg-black/95 backdrop-blur-xl border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl z-50"
                    data-testid="mention-list"
                  >
                    <div className="px-3 py-2 border-b border-white/[0.06]">
                      <p className="text-[10px] text-white/40 font-['Jost'] uppercase tracking-wider">Mencionar</p>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      {getMentionSuggestions().map((member) => (
                        <button
                          key={member.id}
                          onClick={() => handleMentionSelect(member)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.05] transition text-left"
                          data-testid={`mention-${member.id}`}
                        >
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ 
                              background: member.isWIA 
                                ? 'linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(79,70,229,0.2) 100%)' 
                                : 'rgba(255,255,255,0.05)' 
                            }}
                          >
                            <span className={`text-[10px] font-['Jost'] font-semibold ${member.isWIA ? 'text-violet-300' : 'text-white/50'}`}>
                              {member.isWIA ? 'W' : getInitials(member.name)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[13px] font-['Jost'] truncate ${member.isWIA ? 'text-violet-300' : 'text-white/80'}`}>
                              {member.name}
                            </p>
                            {member.cat && (
                              <p className="text-[10px] text-white/40 font-['Jost'] capitalize truncate">{member.cat}</p>
                            )}
                          </div>
                          {member.isWIA && (
                            <div className="flex-shrink-0">
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300 font-['Jost']">IA</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Sensitive content warning */}
              <AnimatePresence>
                {sensitiveWarning && !selfDestructSeconds && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="mx-4 mb-2 p-2.5 rounded-xl flex items-center gap-2"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(234,88,12,0.1) 100%)',
                      border: '1px solid rgba(249,115,22,0.3)'
                    }}
                  >
                    <svg className="w-4 h-4 text-orange-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-orange-400 text-[11px] font-medium">{sensitiveWarning.reason}</p>
                      <p className="text-orange-400/60 text-[10px]">Sugerimos usar mensagem temporária</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelfDestructSeconds(sensitiveWarning.suggestedTime);
                        setSensitiveWarning(null);
                      }}
                      className="px-2.5 py-1 rounded-lg bg-orange-500/20 text-orange-400 text-[10px] font-medium hover:bg-orange-500/30 transition-all whitespace-nowrap"
                    >
                      Ativar
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Textarea with typing effect placeholder */}
              <div className="relative px-3 pt-2.5 pb-1">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    handleInputChange(e);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
                  }}
                  placeholder=""
                  disabled={loading || isRecordingVoice}
                  className="w-full bg-transparent text-[15px] text-white/90 outline-none font-['Jost'] resize-none min-h-[32px] max-h-[80px]"
                  rows={1}
                  onKeyDown={(e) => {
                    // Handle arrow keys for mention navigation
                    if (showMentionList && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                      e.preventDefault();
                      return;
                    }
                    // Handle Tab or Enter to select first mention
                    if (showMentionList && (e.key === 'Tab' || e.key === 'Enter')) {
                      e.preventDefault();
                      const suggestions = getMentionSuggestions();
                      if (suggestions.length > 0) {
                        handleMentionSelect(suggestions[0]);
                      }
                      return;
                    }
                    // Handle Escape to close mention list
                    if (showMentionList && e.key === 'Escape') {
                      e.preventDefault();
                      setShowMentionList(false);
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (input.trim()) {
                        handleSubmit(e);
                      }
                    }
                  }}
                  data-testid="chat-input"
                />
                {/* Custom placeholder with typing effect */}
                {!input && (
                  <div className="absolute top-3 left-4 pointer-events-none">
                    <span className="text-white/35 text-[15px] font-['Jost']">
                      {typingPlaceholder || 'Mensagem...'}
                      <span className="animate-pulse ml-0.5 text-white/50">|</span>
                    </span>
                  </div>
                )}
              </div>
              
              {/* Bottom action bar - clean layout: Clips / W / Mic ... + / Send */}
              <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1">
                  {/* Left icons: Clips / W / Mic */}
                  <div className="flex items-center gap-3">
                    <motion.button whileTap={{ scale: 0.9 }} type="button" onClick={() => fileInputRef.current?.click()} disabled={loading}
                      className="flex items-center justify-center transition touch-manipulation disabled:opacity-30"
                      data-testid="chat-attach-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                    </motion.button>
                    <motion.button whileTap={{ scale: 0.9 }} type="button" onClick={() => setShowWIAModal(true)}
                      className="flex items-center justify-center transition touch-manipulation" data-testid="chat-wia-btn">
                      <span className={`text-[16px] font-['Jost'] font-bold transition-colors ${showWIAModal ? 'text-blue-400' : 'text-white/50'}`}>W</span>
                    </motion.button>
                    {/* Mic / Stop recording */}
                    <motion.button whileTap={{ scale: 0.9 }} type="button" 
                      onClick={isRecordingVoice ? stopVoiceRecording : startVoiceRecording}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition touch-manipulation"
                      style={{ background: isRecordingVoice ? 'rgba(239,68,68,0.15)' : 'transparent', border: isRecordingVoice ? '1px solid rgba(239,68,68,0.3)' : 'none' }}
                      data-testid={isRecordingVoice ? "chat-voice-stop" : "chat-voice-btn"}>
                      {isRecordingVoice ? (
                        <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                        </motion.div>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="2" width="6" height="11" rx="3" /><path d="M19 10v1a7 7 0 01-14 0v-1" /><line x1="12" y1="19" x2="12" y2="22" />
                        </svg>
                      )}
                    </motion.button>
                  </div>
                  {/* Right: + menu and Send */}
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <motion.button whileTap={{ scale: 0.9 }} type="button" onClick={() => setShowPlusMenu(!showPlusMenu)}
                        className="flex items-center justify-center transition touch-manipulation" data-testid="chat-plus-btn">
                        <motion.svg animate={{ rotate: showPlusMenu ? 45 : 0 }} transition={{ duration: 0.2 }}
                          width="18" height="18" viewBox="0 0 24 24" fill="none" 
                          stroke={showPlusMenu ? '#60a5fa' : '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                        </motion.svg>
                      </motion.button>
                      <AnimatePresence>
                        {showPlusMenu && (
                          <>
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              className="fixed inset-0 z-[55]" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowPlusMenu(false)} />
                            
                            {/* Floating icons - Apple Control Center style */}
                            <div className="fixed bottom-20 right-3 left-3 z-[56] flex flex-wrap justify-center gap-4 px-2">
                              {[
                                { 
                                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill={pushEnabled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></svg>,
                                  label: 'Notificações',
                                  active: pushEnabled,
                                  color: '#60a5fa',
                                  action: () => { (pushEnabled ? disablePushNotifications : enablePushNotifications)(); setShowPlusMenu(false); },
                                  delay: 0
                                },
                                { 
                                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                                  label: 'Tradução',
                                  active: translationEnabled,
                                  color: '#34d399',
                                  action: () => { setShowLanguageModal(true); setShowPlusMenu(false); },
                                  delay: 0.02
                                },
                                { 
                                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
                                  label: selfDestructSeconds ? `${selfDestructSeconds}s` : 'Temporária',
                                  active: !!selfDestructSeconds,
                                  color: '#f97316',
                                  action: () => { setShowSelfDestructMenu(true); setShowPlusMenu(false); },
                                  delay: 0.04
                                },
                                { 
                                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>,
                                  label: 'Localização',
                                  active: false,
                                  color: '#22d3ee',
                                  action: () => { setShowLocationModal(true); setShowPlusMenu(false); },
                                  delay: 0.06
                                },
                                { 
                                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
                                  label: 'Criptografar',
                                  active: encryptionEnabled,
                                  color: '#818cf8',
                                  action: () => { setEncryptionEnabled(!encryptionEnabled); setShowPlusMenu(false); },
                                  delay: 0.08
                                },
                              ].map((item, i) => (
                                <motion.button
                                  key={i}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 15 }}
                                  transition={{ type: 'spring', damping: 25, stiffness: 400, delay: item.delay }}
                                  onClick={item.action}
                                  className="flex flex-col items-center gap-1.5 active:scale-90 transition-transform"
                                >
                                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all"
                                    style={{ 
                                      background: 'transparent',
                                      color: item.active ? item.color : 'rgba(255,255,255,0.7)',
                                      filter: item.active ? `drop-shadow(0 0 12px ${item.color}) drop-shadow(0 0 4px ${item.color})` : 'none'
                                    }}>
                                    {item.icon}
                                  </div>
                                  <span className="text-[9px] font-['Jost'] font-medium" style={{ color: item.active ? item.color : 'rgba(255,255,255,0.5)' }}>{item.label}</span>
                                </motion.button>
                              ))}
                            </div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    {/* Send button - iMessage blue circle */}
                    <motion.button 
                      whileTap={{ scale: 0.9 }} 
                      type="submit" 
                      disabled={loading || !input.trim() || isRecordingVoice}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition touch-manipulation flex-shrink-0 ${input.trim() && !encryptionEnabled && !selfDestructSeconds ? 'imsg-send' : ''}`}
                      style={{ 
                        background: input.trim() 
                          ? (encryptionEnabled
                              ? 'linear-gradient(135deg, rgba(129,140,248,0.95) 0%, rgba(99,102,241,0.95) 100%)'
                              : selfDestructSeconds 
                                ? 'linear-gradient(135deg, rgba(249,115,22,0.95) 0%, rgba(234,88,12,0.95) 100%)' 
                                : undefined)
                          : 'rgba(255,255,255,0.12)',
                        border: input.trim() ? 'none' : '1px solid rgba(255,255,255,0.12)',
                        boxShadow: input.trim() 
                          ? (encryptionEnabled ? '0 2px 12px rgba(129,140,248,0.4)' : selfDestructSeconds ? '0 2px 12px rgba(249,115,22,0.4)' : undefined)
                          : 'inset 0 1px 0 rgba(255,255,255,0.05)'
                      }}
                      data-testid="chat-send-button">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" 
                        stroke={input.trim() ? '#ffffff' : 'rgba(255,255,255,0.5)'} 
                        strokeOpacity={1}
                        strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                      </svg>
                    </motion.button>
                  </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>


      {/* Location */}
      <AnimatePresence>
        {showLocationModal && (
          <>
            {/* Liquid Glass backdrop */}
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[59]"
              style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
              onClick={() => { setShowLocationModal(false); setLocationResults([]); setLocationSearch(''); setLocationComment(''); setLocationError(''); }}
            />
            {/* Liquid Glass Sheet */}
            <motion.div 
              initial={{ y: '100%', opacity: 0.5 }} 
              animate={{ y: 0, opacity: 1 }} 
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 32, stiffness: 380 }}
              className="fixed bottom-0 left-0 right-0 z-[60] flex flex-col"
              style={{ 
                maxHeight: '88vh',
                borderRadius: '24px 24px 0 0',
                background: 'linear-gradient(180deg, rgba(30,35,50,0.85) 0%, rgba(15,18,28,0.92) 100%)',
                backdropFilter: 'blur(40px) saturate(1.6)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.6)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderBottom: 'none',
                boxShadow: '0 -8px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 0 80px rgba(34,211,238,0.03)',
              }}
              data-testid="location-modal"
            >
              {/* Drag indicator */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 pb-3">
                <button onClick={() => { setShowLocationModal(false); setLocationResults([]); setLocationSearch(''); setLocationComment(''); setLocationError(''); }}
                  className="text-cyan-400 text-[13px] font-['Jost'] font-medium active:opacity-50 transition" data-testid="location-cancel">Cancelar</button>
                <span className="text-white/90 font-['Jost'] font-semibold text-[15px] tracking-wide">Localização</span>
                <div className="w-14" />
              </div>

              {/* Search bar - Glass */}
              <div className="px-4 pb-3">
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 rounded-2xl"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)' }}>
                    <svg className="w-4 h-4 text-white/25 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input type="text" value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') searchLocation(locationSearch); }}
                      placeholder="Buscar restaurante, bar, hotel..."
                      className="flex-1 bg-transparent text-white placeholder-white/25 outline-none text-[13px] font-['Jost']"
                      data-testid="location-search-input" />
                  </div>
                  {locationSearch && (
                    <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} onClick={() => searchLocation(locationSearch)} 
                      className="px-4 rounded-2xl text-[12px] font-['Jost'] font-medium active:scale-95 transition"
                      style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)', color: '#22d3ee' }}
                      data-testid="location-search-btn">Buscar</motion.button>
                  )}
                </div>
              </div>

              {/* Quick actions - Glass cards */}
              <div className="flex gap-2.5 px-4 pb-3">
                <motion.button whileTap={{ scale: 0.95 }} onClick={sendCurrentLocation}
                  className="flex-1 flex items-center gap-2.5 px-3.5 py-3 rounded-2xl active:opacity-70 transition"
                  style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(34,211,238,0.04) 100%)', border: '1px solid rgba(34,211,238,0.2)', boxShadow: '0 0 20px rgba(34,211,238,0.06)' }}
                  data-testid="send-my-location">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.15)' }}>
                    <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                  </div>
                  <div className="text-left"><p className="text-cyan-300 text-[12px] font-['Jost'] font-semibold leading-tight">Minha localização</p><p className="text-white/25 text-[9px] font-['Jost']">GPS</p></div>
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={loadNearby}
                  className="flex-1 flex items-center gap-2.5 px-3.5 py-3 rounded-2xl active:opacity-70 transition"
                  style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.04) 100%)', border: '1px solid rgba(99,102,241,0.2)', boxShadow: '0 0 20px rgba(99,102,241,0.06)' }}
                  data-testid="load-nearby">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                    <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <div className="text-left"><p className="text-indigo-300 text-[12px] font-['Jost'] font-semibold leading-tight">Próximos</p><p className="text-white/25 text-[9px] font-['Jost']">Restaurantes, bares</p></div>
                </motion.button>
              </div>

              {/* Comment input */}
              <div className="px-4 pb-2">
                <input type="text" value={locationComment} onChange={(e) => setLocationComment(e.target.value)} 
                  placeholder="Adicionar comentário..."
                  className="w-full px-3.5 py-2 rounded-xl text-white placeholder-white/20 outline-none text-[12px] font-['Jost']"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                  data-testid="location-comment" />
              </div>

              {/* Results area */}
              <div className="flex-1 overflow-y-auto px-4 pb-8" style={{ maxHeight: '45vh' }}>
                {locationError && <div className="py-4 text-center"><p className="text-red-400/60 text-[11px] font-['Jost']">{locationError}</p></div>}
                
                {locationSearching && (
                  <div className="py-8 text-center">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                      className="w-7 h-7 mx-auto rounded-full" style={{ border: '2px solid rgba(34,211,238,0.15)', borderTopColor: 'rgba(34,211,238,0.8)' }} />
                    <p className="text-white/20 text-[10px] mt-3 font-['Jost']">Localizando...</p>
                  </div>
                )}

                {!locationSearching && locationResults.length > 0 && (
                  <motion.div initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}>
                    <p className="text-white/30 text-[10px] font-['Jost'] uppercase tracking-widest mb-2">{locationResults.length} locais</p>
                    {locationResults.map((place, i) => {
                      const labels = { restaurant: 'Restaurante', bar: 'Bar', cafe: 'Café', pub: 'Pub', nightclub: 'Balada', hotel: 'Hotel', guest_house: 'Pousada', fast_food: 'Fast Food', wine: 'Vinhos', tobacco: 'Tabacaria' };
                      return (
                        <motion.button 
                          key={place.id || i}
                          variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
                          onClick={async () => {
                            if (!currentMember) return;
                            const u = `https://maps.google.com/maps?q=${place.lat},${place.lon}`;
                            const msg = `📍 ${place.name}\n${place.address}\n${u}${locationComment.trim() ? `\n💬 ${locationComment.trim()}` : ''}`;
                            await sendChatMessage({ sender_id: currentMember.id, sender_name: currentMember.name, room: 'general', recipient_id: selectedRecipient?.id || null, message: msg, message_type: 'location' });
                            loadMessages(); setShowLocationModal(false); setLocationResults([]); setLocationSearch(''); setLocationComment(''); setLocationError('');
                          }} 
                          className="w-full flex items-center gap-3 py-3 border-b border-white/[0.04] active:bg-white/[0.04] text-left rounded-xl transition"
                          data-testid={`location-result-${i}`}
                        >
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.12)' }}>
                            <svg className="w-4 h-4 text-cyan-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white/90 text-[13px] font-['Jost'] font-medium truncate">{place.name}</p>
                            <p className="text-white/30 text-[10px] font-['Jost'] truncate">{place.address}</p>
                          </div>
                          {labels[place.type] && <span className="text-[8px] text-cyan-400/40 uppercase tracking-wider font-['Jost'] bg-cyan-400/[0.06] px-2 py-0.5 rounded-full flex-shrink-0 border border-cyan-400/[0.08]">{labels[place.type]}</span>}
                        </motion.button>
                      );
                    })}
                  </motion.div>
                )}

                {!locationSearching && locationResults.length === 0 && !locationError && (
                  <div className="text-center py-8">
                    <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                      <svg className="w-10 h-10 text-white/8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                      </svg>
                    </motion.div>
                    <p className="text-white/15 text-[11px] font-['Jost'] mt-3">Carregando locais próximos...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Self-destruct menu modal */}
      <AnimatePresence>
        {showSelfDestructMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
              style={{ background: 'rgba(0,0,0,0.5)' }}
              onClick={() => setShowSelfDestructMenu(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-4 right-4 bottom-24 z-[101] rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, rgba(25,25,35,0.98) 0%, rgba(15,15,22,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(249,115,22,0.25)',
                boxShadow: '0 0 40px rgba(249,115,22,0.2), 0 20px 60px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-3">
                <div className="flex items-center gap-2 px-2 pb-2 mb-2 border-b border-white/10">
                  <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-white/80 text-sm font-['Jost'] font-medium">Mensagem Temporária</p>
                </div>
                <p className="text-white/40 text-[11px] px-2 mb-3 font-['Jost']">A mensagem será apagada automaticamente após o tempo selecionado</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: '1 min', seconds: 60 },
                    { label: '10 min', seconds: 600 },
                    { label: '1 hora', seconds: 3600 },
                    { label: '12 horas', seconds: 43200 },
                    { label: '24 horas', seconds: 86400 },
                  ].map((opt) => (
                    <button
                      key={opt.seconds}
                      type="button"
                      onClick={() => {
                        setSelfDestructSeconds(opt.seconds);
                        setShowSelfDestructMenu(false);
                      }}
                      className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl transition-all ${
                        selfDestructSeconds === opt.seconds 
                          ? 'bg-orange-500/30 border-orange-500/50' 
                          : 'bg-white/5 border-white/10 active:bg-white/10'
                      } border`}
                    >
                      <svg className={`w-5 h-5 ${selfDestructSeconds === opt.seconds ? 'text-orange-400' : 'text-white/50'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className={`text-[11px] font-['Jost'] ${selfDestructSeconds === opt.seconds ? 'text-orange-400 font-medium' : 'text-white/70'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                  {selfDestructSeconds && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelfDestructSeconds(null);
                        setShowSelfDestructMenu(false);
                      }}
                      className="flex flex-col items-center gap-1 px-3 py-3 rounded-xl transition-all bg-red-500/10 border border-red-500/30 active:bg-red-500/20"
                    >
                      <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-[11px] font-['Jost'] text-red-400">Desativar</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Context Menu - W Specialist Style, Emerges from clicked message */}
      <AnimatePresence>
        {contextMenu && (
          <>
            {/* Backdrop overlay - darker to make tooltip "illuminate" */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/70"
              onClick={() => setContextMenu(null)}
            />
            
            {/* Menu - W Specialist violet glow style */}
            <motion.div
              initial={{ opacity: 0, scale: 0.3 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.3 }}
              transition={{ type: "spring", damping: 22, stiffness: 350 }}
              className="fixed z-50 w-[75%] max-w-[260px] rounded-2xl overflow-hidden"
              style={{
                left: Math.min(Math.max(contextMenu.x - 130, 16), window.innerWidth - 276),
                top: Math.min(Math.max(contextMenu.y - 80, 80), window.innerHeight - 320),
                transformOrigin: 'center center',
                background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(139,92,246,0.05) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1.5px solid rgba(139,92,246,0.45)',
                boxShadow: '0 0 30px rgba(139,92,246,0.35), 0 0 60px rgba(139,92,246,0.15), inset 0 1px 0 rgba(139,92,246,0.20)',
              }}
              data-testid="context-menu"
            >
              {/* Quick Reactions Bar */}
              <div className="flex items-center justify-around px-3 py-3 border-b border-violet-500/20">
                {Object.entries(REACTION_CONFIG).map(([reactionType, config]) => {
                  const Icon = ReactionIcons[reactionType];
                  const isActive = hasReacted(contextMenu.msg, reactionType);
                  return (
                    <motion.button
                      key={reactionType}
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleReaction(contextMenu.msg, reactionType)}
                      className="flex items-center justify-center p-1.5 rounded-full transition-all"
                      style={{ 
                        opacity: isActive ? 1 : 0.8,
                        background: isActive ? 'rgba(139,92,246,0.25)' : 'transparent'
                      }}
                      title={config.label}
                      data-testid={`reaction-${reactionType}`}
                    >
                      {Icon && <Icon size={18} filled={false} color={config.color} />}
                    </motion.button>
                  );
                })}
              </div>
              
              {/* Menu Options */}
              <div className="py-1">
                {/* Unlock option - for encrypted messages that are NOT decrypted */}
                {(contextMenu.msg.message_type === 'encrypted' || contextMenu.msg.message_type === 'encrypted_image' || contextMenu.msg.message_type === 'encrypted_file') && !decryptedMessages[contextMenu.msg.id] && (
                  <button
                    onClick={() => {
                      const encryptedContent = contextMenu.msg.message_type === 'encrypted' 
                        ? contextMenu.msg.message 
                        : contextMenu.msg.file_data;
                      setDecryptModal({ messageId: contextMenu.msg.id, encryptedText: encryptedContent });
                      setContextMenu(null);
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-violet-300 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                    data-testid="ctx-unlock"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    Desbloquear
                  </button>
                )}
                
                {/* Lock option - for decrypted messages to lock them again */}
                {(contextMenu.msg.message_type === 'encrypted' || contextMenu.msg.message_type === 'encrypted_image' || contextMenu.msg.message_type === 'encrypted_file') && decryptedMessages[contextMenu.msg.id] && (
                  <button
                    onClick={() => {
                      setDecryptedMessages(prev => {
                        const newState = { ...prev };
                        delete newState[contextMenu.msg.id];
                        return newState;
                      });
                      setContextMenu(null);
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-violet-300 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                    data-testid="ctx-lock"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Bloquear
                  </button>
                )}
                
                <button
                  onClick={() => handleCopyMessage(contextMenu.msg)}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-white/85 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                  data-testid="ctx-copy"
                >
                  <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copiar
                </button>
                
                <button
                  onClick={() => handleShareMessage(contextMenu.msg)}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-white/85 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                  data-testid="ctx-share"
                >
                  <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Compartilhar
                </button>
                
                {/* Parar conversa com W */}
                {contextMenu.msg.sender_id === 'w-ia' && wiaConversationActive && (
                  <button
                    onClick={() => {
                      stopWiaConversation();
                      setContextMenu(null);
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-violet-300 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                    data-testid="ctx-stop-wia"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    Parar conversa com W
                  </button>
                )}
            
            {/* Excluir mensagem do W */}
            {contextMenu.msg.sender_id === 'w-ia' && (
              <button
                onClick={() => handleDeleteMessage(contextMenu.msg)}
                className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-red-400/80 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                data-testid="ctx-delete-wia"
              >
                <svg className="w-4 h-4 text-red-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Excluir mensagem
              </button>
            )}
            
            {/* Delete own messages */}
            {contextMenu.msg.sender_id === currentMember?.id && (
              <button
                onClick={() => handleDeleteMessage(contextMenu.msg)}
                className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-red-400/80 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                data-testid="ctx-delete"
              >
                <svg className="w-4 h-4 text-red-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Deletar
              </button>
            )}
            
            {/* Delete messages from others (only in DM) - shows as "Apagar para mim" */}
            {contextMenu.msg.sender_id !== currentMember?.id && contextMenu.msg.sender_id !== 'w-ia' && selectedRecipient && (
              <button
                onClick={() => handleDeleteMessage(contextMenu.msg)}
                className="w-full px-4 py-2.5 flex items-center gap-3 text-left text-[13px] text-red-400/80 hover:bg-violet-500/15 active:bg-violet-500/25 transition font-['Jost']"
                data-testid="ctx-delete-other"
              >
                <svg className="w-4 h-4 text-red-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Apagar para mim
              </button>
            )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Call Overlay */}
      <AnimatePresence>
        {activeCall && (
          <CallOverlay callId={activeCall.id} callerName={activeCall.callerName} calleeName={activeCall.calleeName} isCaller={activeCall.isCaller} onEnd={() => setActiveCall(null)} />
        )}
      </AnimatePresence>

      {/* Decrypt Modal - Liquid glass violet style matching encrypted bubble */}
      <AnimatePresence>
        {decryptModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => { setDecryptModal(null); setDecryptPin(['', '', '', '']); setDecryptStatus(null); setActiveDigitIndex(0); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-2xl p-5 w-[230px] backdrop-blur-xl"
              style={{ 
                background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.08))',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
              }}
            >
              {/* PIN Input boxes with status feedback */}
              <div className="flex justify-center gap-3 mb-4">
                {decryptStatus === 'success' ? (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.4 }}
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.3), rgba(124, 58, 237, 0.2))',
                      boxShadow: '0 0 30px rgba(139, 92, 246, 0.4), inset 0 0 20px rgba(139, 92, 246, 0.2)'
                    }}
                  >
                    <motion.svg 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgb(167, 139, 250)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </motion.svg>
                  </motion.div>
                ) : decryptStatus === 'error' ? (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1], rotate: [0, -15, 15, -15, 15, 0] }}
                    transition={{ duration: 0.5 }}
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(145deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.2))',
                      boxShadow: '0 0 30px rgba(239, 68, 68, 0.4), inset 0 0 20px rgba(239, 68, 68, 0.2)'
                    }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgb(248, 113, 113)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </motion.div>
                ) : (
                  [0, 1, 2, 3].map((i) => (
                    <div 
                      key={i}
                      onClick={() => {
                        setActiveDigitIndex(i);
                        pinInputRefs.current[i]?.focus();
                      }}
                      className="relative w-11 h-14 rounded-xl flex items-center justify-center text-xl font-['Jost'] text-white cursor-pointer transition-all"
                      style={{ 
                        background: activeDigitIndex === i 
                          ? 'linear-gradient(145deg, rgba(139, 92, 246, 0.25), rgba(124, 58, 237, 0.15))' 
                          : 'rgba(139, 92, 246, 0.08)',
                        border: activeDigitIndex === i 
                          ? '1.5px solid rgba(139, 92, 246, 0.6)' 
                          : decryptPin[i] 
                            ? '1px solid rgba(139, 92, 246, 0.4)' 
                            : '1px solid rgba(255,255,255,0.1)',
                        boxShadow: activeDigitIndex === i ? '0 0 15px rgba(139, 92, 246, 0.2)' : 'none'
                      }}
                    >
                      <input
                        ref={(el) => pinInputRefs.current[i] = el}
                        type="text"
                        maxLength={1}
                        value={decryptPin[i]}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(-1);
                          setDecryptPin(prev => {
                            const newPin = [...prev];
                            newPin[i] = val;
                            return newPin;
                          });
                          // Auto-advance to next input
                          if (val && i < 3) {
                            setActiveDigitIndex(i + 1);
                            pinInputRefs.current[i + 1]?.focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !decryptPin[i] && i > 0) {
                            // Go back to previous input on backspace if current is empty
                            setActiveDigitIndex(i - 1);
                            pinInputRefs.current[i - 1]?.focus();
                          } else if (e.key === 'Enter' && decryptPin.every(d => d)) {
                            handleDecryptAttempt();
                          }
                        }}
                        onFocus={() => setActiveDigitIndex(i)}
                        autoFocus={i === 0}
                        autoCapitalize="characters"
                        autoComplete="off"
                        className="absolute inset-0 w-full h-full bg-transparent text-center text-xl font-['Jost'] text-white outline-none"
                        style={{ caretColor: 'rgba(139, 92, 246, 0.8)' }}
                      />
                    </div>
                  ))
                )}
              </div>
              
              {/* Unlock button - blue accent */}
              {!decryptStatus && (
                <button
                  type="button"
                  onClick={handleDecryptAttempt}
                  disabled={!decryptPin.every(d => d)}
                  className="w-full h-12 rounded-xl font-['Jost'] text-sm flex items-center justify-center gap-2 transition-all touch-manipulation disabled:opacity-30"
                  style={{ 
                    background: decryptPin.every(d => d) 
                      ? 'linear-gradient(145deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.8))'
                      : 'rgba(255,255,255,0.08)',
                    color: decryptPin.every(d => d) ? 'white' : 'rgba(255,255,255,0.4)',
                    boxShadow: decryptPin.every(d => d) ? '0 4px 20px rgba(59, 130, 246, 0.3)' : 'none'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                  </svg>
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* @Wcash Transfer Modal */}
      {showWcashModal && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/70"
            onClick={() => { setShowWcashModal(false); setWcashAmount(''); setWcashRecipient(''); }}
          />
          <div
            className="fixed z-[9999] left-4 right-4 top-1/2 -translate-y-1/2"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="rounded-2xl p-5 mx-auto max-w-[340px]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                boxShadow: '0 0 60px rgba(34, 197, 94, 0.15), 0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[16px] font-['Jost'] font-semibold text-white">Transferir Wcash</h3>
                  <p className="text-[11px] font-['Jost'] text-white/50">Enviar para outro membro</p>
                </div>
              </div>
              
              {/* Amount Input */}
              <div className="mb-3">
                <label className="text-[11px] font-['Jost'] text-white/50 uppercase tracking-wider mb-1.5 block">Valor</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-400 font-['Jost'] font-semibold">W$</span>
                  <input
                    type="number"
                    value={wcashAmount}
                    onChange={(e) => setWcashAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white font-['Jost'] text-[15px] focus:outline-none focus:border-green-500/50 transition"
                  />
                </div>
              </div>
              
              {/* Recipient Input */}
              <div className="mb-4">
                <label className="text-[11px] font-['Jost'] text-white/50 uppercase tracking-wider mb-1.5 block">Para quem</label>
                <input
                  type="text"
                  value={wcashRecipient}
                  onChange={(e) => setWcashRecipient(e.target.value)}
                  placeholder="Nome do membro"
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white font-['Jost'] text-[15px] focus:outline-none focus:border-green-500/50 transition"
                />
              </div>
              
              {/* Info */}
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 mb-4">
                <p className="text-[11px] font-['Jost'] text-green-400/80">
                  Um comprovante será enviado automaticamente para o destinatário
                </p>
              </div>
              
              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowWcashModal(false); setWcashAmount(''); setWcashRecipient(''); }}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-medium text-white/60 bg-white/5 hover:bg-white/10 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeWcashTransfer}
                  disabled={actionLoading || !wcashAmount || !wcashRecipient}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-semibold flex items-center justify-center gap-2 bg-green-500/30 text-green-400 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* @Recado Reminder Modal */}
      {showRecadoModal && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/70"
            onClick={() => { setShowRecadoModal(false); setRecadoText(''); }}
          />
          <div
            className="fixed z-[9999] left-4 right-4 top-1/2 -translate-y-1/2"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="rounded-2xl p-5 mx-auto max-w-[340px]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                boxShadow: '0 0 60px rgba(251, 191, 36, 0.15), 0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(251, 191, 36, 0.2)' }}>
                  <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[16px] font-['Jost'] font-semibold text-white">Criar Lembrete</h3>
                  <p className="text-[11px] font-['Jost'] text-white/50">Recado para você mesmo</p>
                </div>
              </div>
              
              {/* Recado Text Input */}
              <div className="mb-3">
                <label className="text-[11px] font-['Jost'] text-white/50 uppercase tracking-wider mb-1.5 block">O que lembrar?</label>
                <textarea
                  value={recadoText}
                  onChange={(e) => setRecadoText(e.target.value)}
                  placeholder="Ex: Ligar pro João"
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white font-['Jost'] text-[14px] focus:outline-none focus:border-amber-500/50 transition resize-none"
                />
              </div>
              
              {/* Time Selection */}
              <div className="mb-4">
                <label className="text-[11px] font-['Jost'] text-white/50 uppercase tracking-wider mb-1.5 block">Quando?</label>
                <div className="flex flex-wrap gap-2">
                  {['1h', '2h', '6h', 'Amanhã', '1 Semana'].map((time) => (
                    <button
                      key={time}
                      onClick={() => setRecadoTime(time.toLowerCase())}
                      className={`px-3 py-1.5 rounded-lg text-[12px] font-['Jost'] transition ${
                        recadoTime === time.toLowerCase()
                          ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                          : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowRecadoModal(false); setRecadoText(''); }}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-medium text-white/60 bg-white/5 hover:bg-white/10 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeRecado}
                  disabled={actionLoading || !recadoText}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-semibold flex items-center justify-center gap-2 bg-amber-500/30 text-amber-400 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Criar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* @Conversa Summary Modal */}
      {showConversaModal && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/70"
            onClick={() => setShowConversaModal(false)}
          />
          <div
            className="fixed z-[9999] left-4 right-4 top-1/2 -translate-y-1/2"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="rounded-2xl p-5 mx-auto max-w-[340px]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                boxShadow: '0 0 60px rgba(59, 130, 246, 0.15), 0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.2)' }}>
                  <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[16px] font-['Jost'] font-semibold text-white">Resumo da Conversa</h3>
                  <p className="text-[11px] font-['Jost'] text-white/50">IA analisa as mensagens</p>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-4">
                <p className="text-[13px] font-['Jost'] text-white/80">
                  A IA vai analisar as últimas 50 mensagens e gerar um resumo com:
                </p>
                <ul className="mt-2 space-y-1 text-[12px] font-['Jost'] text-blue-400/80">
                  <li>• Principais tópicos</li>
                  <li>• Decisões tomadas</li>
                  <li>• Itens de ação</li>
                </ul>
              </div>
              
              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowConversaModal(false)}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-medium text-white/60 bg-white/5 hover:bg-white/10 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeConversa}
                  disabled={actionLoading}
                  className="flex-1 py-3 rounded-xl text-[13px] font-['Jost'] font-semibold flex items-center justify-center gap-2 bg-blue-500/30 text-blue-400 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Gerar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reminders Modal */}
      <AnimatePresence>
        {showRemindersModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
              style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={() => setShowRemindersModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-4 right-4 bottom-8 z-[101] rounded-2xl overflow-hidden max-h-[70vh]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(251,191,36,0.2)',
                boxShadow: '0 0 40px rgba(251,191,36,0.1), 0 20px 60px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                         style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.3) 0%, rgba(251,191,36,0.1) 100%)' }}>
                      <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-['Jost'] font-semibold text-white">Meus Lembretes</h3>
                      <p className="text-[11px] font-['Jost'] text-white/50">{reminders.length} lembrete{reminders.length !== 1 ? 's' : ''} pendente{reminders.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowRemindersModal(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 transition"
                  >
                    <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Reminders List */}
              <div className="px-4 py-3 overflow-y-auto" style={{ maxHeight: '50vh' }}>
                {reminders.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-white/20 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[13px] font-['Jost'] text-white/40">Nenhum lembrete pendente</p>
                    <p className="text-[11px] font-['Jost'] text-white/30 mt-1">Digite "me lembra de..." no chat</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reminders.map((reminder) => (
                      <div key={reminder.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-[13px] font-['Jost'] text-white/90">{reminder.message}</p>
                            <p className="text-[10px] font-['Jost'] text-amber-400/70 mt-1">
                              ⏰ {new Date(reminder.remind_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => completeReminder(reminder.id)}
                              className="w-7 h-7 rounded-full flex items-center justify-center bg-green-500/20 hover:bg-green-500/30 transition"
                            >
                              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </motion.button>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => deleteReminder(reminder.id)}
                              className="w-7 h-7 rounded-full flex items-center justify-center bg-red-500/20 hover:bg-red-500/30 transition"
                            >
                              <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Conversation Summary Modal */}
      <AnimatePresence>
        {showSummaryModal && conversationSummary && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
              style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={() => setShowSummaryModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-4 right-4 bottom-8 z-[101] rounded-2xl overflow-hidden max-h-[80vh]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(59,130,246,0.2)',
                boxShadow: '0 0 40px rgba(59,130,246,0.1), 0 20px 60px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                         style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(59,130,246,0.1) 100%)' }}>
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-['Jost'] font-semibold text-white">Resumo da Conversa</h3>
                      <p className="text-[11px] font-['Jost'] text-white/50">{conversationSummary.message_count} mensagens analisadas</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowSummaryModal(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 transition"
                  >
                    <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Summary Content */}
              <div className="px-4 py-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                <div className="text-[13px] font-['Jost'] text-white/80 leading-relaxed whitespace-pre-wrap">
                  {conversationSummary.summary}
                </div>
                
                {conversationSummary.period && (
                  <div className="mt-4 pt-3 border-t border-white/5">
                    <p className="text-[10px] font-['Jost'] text-white/40">
                      Período: {conversationSummary.period.from ? new Date(conversationSummary.period.from).toLocaleDateString('pt-BR') : '?'} - {conversationSummary.period.to ? new Date(conversationSummary.period.to).toLocaleDateString('pt-BR') : '?'}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Language Selection Modal */}
      <AnimatePresence>
        {showLanguageModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
              style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={() => setShowLanguageModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-4 right-4 bottom-8 z-[101] rounded-2xl overflow-hidden max-h-[70vh]"
              style={{
                background: 'linear-gradient(145deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(59,130,246,0.2)',
                boxShadow: '0 0 40px rgba(59,130,246,0.15), 0 20px 60px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                         style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(59,130,246,0.1) 100%)' }}>
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-['Jost'] font-semibold text-white">Tradução em Tempo Real</h3>
                      <p className="text-[11px] font-['Jost'] text-white/50">Mensagens serão traduzidas automaticamente</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowLanguageModal(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 transition"
                  >
                    <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Toggle Translation */}
              <div className="px-4 py-3 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-['Jost'] text-white/90">Tradução automática</p>
                    <p className="text-[10px] font-['Jost'] text-white/40">Traduzir mensagens de outros idiomas</p>
                  </div>
                  <button
                    onClick={() => setTranslationEnabled(!translationEnabled)}
                    className={`w-12 h-6 rounded-full transition-all ${translationEnabled ? 'bg-blue-500' : 'bg-white/10'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-lg transform transition-all ${translationEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>
              
              {/* Current Language Display */}
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-[10px] font-['Jost'] text-white/40 uppercase tracking-wider mb-2">Seu idioma</p>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <span className="text-lg">{userLanguage === 'pt' ? '🇧🇷' : userLanguage === 'en' ? '🇺🇸' : userLanguage === 'es' ? '🇪🇸' : '🌐'}</span>
                  <span className="text-[13px] font-['Jost'] text-blue-300 font-medium">
                    {supportedLanguages.find(l => l.code === userLanguage)?.name || userLanguage?.toUpperCase() || 'Detectando...'}
                  </span>
                  <svg className="w-4 h-4 text-blue-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              {/* Language Selection */}
              <div className="px-4 py-3 overflow-y-auto" style={{ maxHeight: '35vh' }}>
                <p className="text-[10px] font-['Jost'] text-white/40 uppercase tracking-wider mb-2">Selecionar idioma</p>
                <div className="grid grid-cols-2 gap-2">
                  {supportedLanguages.map((lang) => {
                    const isSelected = userLanguage === lang.code;
                    const flags = { pt: '🇧🇷', en: '🇺🇸', es: '🇪🇸', fr: '🇫🇷', it: '🇮🇹', de: '🇩🇪', ja: '🇯🇵', zh: '🇨🇳', ko: '🇰🇷', ar: '🇸🇦', ru: '🇷🇺', hi: '🇮🇳', nl: '🇳🇱', pl: '🇵🇱', tr: '🇹🇷' };
                    return (
                      <motion.button
                        key={lang.code}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => changeUserLanguage(lang.code)}
                        className={`flex items-center gap-2 p-2.5 rounded-xl transition-all ${
                          isSelected 
                            ? 'bg-blue-500/20 border border-blue-500/40' 
                            : 'bg-white/[0.03] border border-transparent hover:bg-white/[0.06]'
                        }`}
                      >
                        <span className="text-base">{flags[lang.code] || '🌐'}</span>
                        <span className={`text-[12px] font-['Jost'] ${isSelected ? 'text-blue-300' : 'text-white/70'}`}>
                          {lang.name}
                        </span>
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 text-blue-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
              
              {/* Info Footer */}
              <div className="px-4 py-3 bg-white/[0.02]">
                <p className="text-[10px] font-['Jost'] text-white/30 text-center">
                  Powered by IA • Tradução instantânea para +30 idiomas
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mini W IA Embutido - Chat completo dentro do Wchat */}
      <AnimatePresence>
        {showWIAModal && (
          <MiniWIA 
            currentMember={currentMember}
            onClose={() => setShowWIAModal(false)}
          />
        )}
      </AnimatePresence>

      {/* W Aviso Tooltip - Liquid Glass centered */}
      <AnimatePresence>
        {activeWAviso && (
          <WAvisoTooltip
            aviso={activeWAviso}
            onClose={handleDismissAviso}
            onDelete={handleDeleteAviso}
          />
        )}
      </AnimatePresence>
    </div>
    </div>
    </div>
  );
};

export default ChatSection;
